import type { BgMove, BgSpot, BgView } from "@bing/engine-backgammon";
import { NO_MOVE_NOTICE_MS, ROUND_HOLD_MS } from "@bing/engine-backgammon";
import {
  useContext,
  createContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useBoxHeight } from "../game/use-fit-square";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { GameIcon } from "../components/GameIcon";
import { OfficialBadge } from "../components/OfficialBadge";
import { displayName, type DisplayNameParts } from "../components/profile-ui";
import { useBackgammonSfx } from "../game/backgammon-sfx";
import { prefersReducedMotion, SlideChecker, WALK_MS } from "../game/backgammon-motion";
import { DIE_THROW_MS, Die3D } from "../game/backgammon-dice";
import { HIT_MS } from "../game/backgammon-replay";
import { useBgReplay } from "../game/use-bg-replay";
import { fa } from "../i18n/fa";
import type { BackgammonCheckerColor } from "../settings/prefs";
import { playSfx } from "../sound/sfx";

/* ------------------------------------------------------------------ *
 * Backgammon (تخته نرد) — the SOLO (human-vs-bot) tap-to-move board,
 * PORTRAIT. Built on the same table chrome as ChessTable (a gold-framed
 * felt with an exit button in the header) but adapted to a backgammon
 * board: two rows of twelve points (6·BAR·6 each), a bear-off rack that
 * is a SIBLING of the felt (never an overlay on a point), plus an
 * opponent + own strip carrying the identity and (online, timed) a time
 * bank. NO pip count — the traditional Iranian game has no such thing. The dice are real 3D cubes (game/backgammon-dice)
 * thrown into the ROLLER's half — a die must come to rest on the
 * roller's own section of the board, so they never sit on the bar.
 *
 * The board is oriented from `view.you`'s seat — each viewer sees their
 * own home board on the tray's side. Tap-to-move is driven
 * by the server-authoritative `legalFrom`/`legalTo`: tap a source in
 * `legalFrom` → its destinations glow → tap one to send `{kind:"move"}`;
 * a barred checker forces the bar as the only source (§5.3). Context
 * pills carry roll / undo / confirm exactly when the view offers them.
 *
 * PHASE-2 PLACEHOLDER: chips/dice/points render with clean inline styles
 * from the shared design tokens; every checker funnels through the single
 * {@link Chip}/{@link Stack} helper and the `bg-*` class hooks are the
 * seam Phase 4 swaps for CSS-gradient triangles + the central well + the
 * §11 motions — in ONE place, nothing else renders geometry.
 * ------------------------------------------------------------------ */

/** Latin→Persian digits, to match the rest of the app (point labels, counts, pip). */
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const;
function faNum(value: number | string): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/* ------------------------------------------------------------------ *
 * BOARD GEOMETRY — solved, not guessed.
 *
 * The board used to be `0.44 × the measured box`: a magic factor tuned on one phone. Measured
 * consequence across 10 viewports: the point ratio (height ÷ width) landed at 5.6 on an iPhone 12
 * but **3.4 on Telegram Desktop 440×640** — stubby points on a flat board — while the board itself
 * occupied only 36–39% of the screen with 135–339px of slack going nowhere.
 *
 * The layout actually obeys a fixed relation, derived from the real DOM (board padding 4, gap 4,
 * tray 26; felt borders 1, bar 20; each half-row `padding: 0 4px`; point `width:16%` and
 * `height:85%` of a half-height row):
 *
 *     ratio = 5.3125 × (feltH − 2) / (feltW − 38),   feltW = boardW − 38,  feltH = boardH − 8
 *
 * Validated against a real browser: predicts 5.63 vs 5.58 measured (iPhone 12) and 3.51 vs 3.43
 * (Telegram Desktop 440×640). So the board is SOLVED for a target ratio, which fixes PC and the
 * small phones without disturbing the tall-phone case that was already right.
 * ------------------------------------------------------------------ */
/* ── BOARD GEOMETRY — the midfield band ───────────────────────────────────────
 *
 * REBUILT 2026-08-09. The old model derived the board's HEIGHT from its WIDTH through a fixed
 * constant (`RATIO_K = 5.3125`, itself derived from the old chrome), which meant a portrait phone
 * could never spend its surplus height: the board sat at ~37% of the screen with ~250px dead above
 * and ~330px below, and the dice had nowhere to rest except on top of the checkers.
 *
 * The new model gives the surplus to a MIDFIELD BAND — the empty strip across the middle of the
 * felt, between the two rows of points (measured on the reference board: points ≈39% of the felt
 * from each rail, band ≈21%). The band is where the dice rest and where toasts appear, so neither
 * can ever overlap a checker, and the board can fill the screen WITHOUT stretching the points into
 * needles. Points keep their real-board proportion; only the band breathes.
 *
 *   pointW = (feltW − spine) / 12          ← width is always fully used
 *   pointH = clamp(ideal, 5.0·pointW, 6.2·pointW)
 *   feltH  = 2·pointH + band               ← band absorbs what is left
 *   boardH = feltH + 2·rail                ← rails hold the bear-off pockets
 */

/** Rim thickness left/right of the felt. */
const RIM_X = 7;
/** Top/bottom rails — deep enough to sink a bear-off pocket into (the reference keeps its trays
 *  in the rails, which is what frees the felt to use the whole width). */
const RAIL_H = 30;
/**
 * The central bar/spine between the two halves, AS A MULTIPLE OF A POINT'S WIDTH.
 *
 * ⚠️ IT USED TO BE A FIXED 22px, AND A CHECKER DID NOT FIT IN IT. A checker is sized from the
 * MEASURED point width (`--pt-w`), which on a 440px-wide board solves to ~32px — so a blot that
 * got hit landed on a bar NARROWER THAN ITSELF and hung over both edges of the channel. Deriving
 * the spine from the same number makes "a checker fits on the bar" a property of the layout rather
 * than a coincidence of one phone's width.
 */
const SPINE_RATIO = 1.2;
/** The proportion accepted in round 3 ("real-board proportion"). */
export const TARGET_POINT_RATIO = 5.5;
/** Points may breathe this much to fill a viewport before the band takes over. */
const MIN_POINT_RATIO = 5.0;
const MAX_POINT_RATIO = 6.2;
/** The band's share of the felt (reference ≈21%), and the limits it may not leave. */
const BAND_FRACTION = 0.22;
const BAND_MIN_PX = 62;
const BAND_MAX_FRACTION = 0.3;
/** Never let a rounding error re-introduce the clipping that bit منچ. */
const FIT_SAFETY_PX = 6;

/** Panel + action rows are FIXED height and always rendered — a row that collapses when empty is
 *  what used to resize the board between turns (measured 49px of swing).
 *
 *  62 → 52: the panel's second line used to carry a pip count as well, which forced the taller box.
 *  With the pip gone (traditional تخته نرد has no such thing) the details fit one shorter line, and
 *  the 10px it gives back go under the panel, where the home indicator lives. */
const PANEL_H = 52;
const ACTION_W = 80;
/**
 * The panel's trailing column — the countdown ring over the «نوبت شماست» label.
 *
 * ⚠️ MEASURED, not chosen: the label is `white-space: nowrap` at 9.5px bold, and «نوبت شماست»
 * renders ~58px wide, so a 52px column let it spill back over the name/avatar it sits beside. The
 * column is now wide enough for the widest of the two labels, and the RING is sized to leave room
 * for it inside {@link PANEL_H} (ring + gap + label must clear 52px, not fill it).
 */
const TURN_COL_W = 62;
/** The ring inside that column. 38 filled the panel and crowded the label; 26 reads as a countdown
 *  without competing with the profile beside it. */
const TURN_RING_PX = 26;
/** The board's own bottom clearance. Every other table pads by the safe-area inset; this one did
 *  NOT, so on a phone the player's own panel sat under the home indicator (measured: 4px of gap
 *  above the viewport edge at 360×640, and that harness reports a ZERO inset). */
const BOARD_PAD_BOTTOM = 18;

/** The seam between two checkers in a stack, and a point's own top/bottom padding.
 *
 *  ⚠️ SHARED, deliberately: the stack's seating and the FLIGHT's endpoints must agree, or a
 *  travelling checker lands where no checker sits. 4px of gap also made a five-stack 10px longer
 *  than the point it sits in at the ratio floor — measured 9px past the apex on Telegram Desktop —
 *  so real checkers touch, as they do on a physical board. */
export const CHIP_GAP = 1;
const POINT_PAD = 2;
/** Undo's own always-reserved cell, so the primary never shares a slot and never moves. */
const UNDO_W = 46;

export interface BoardGeometry {
  /** The board box, rim included. */
  w: number;
  h: number;
  feltW: number;
  feltH: number;
  pointW: number;
  /** The central channel — always wider than a checker (see {@link SPINE_RATIO}). */
  spineW: number;
  /** The length of a point — what a checker stack runs along. */
  pointH: number;
  /** The empty strip across the middle: dice + toasts live here, nothing else. */
  bandH: number;
  /** pointH / pointW — the "does it read as a triangle or a needle" number. */
  ratio: number;
}

/** Solve the board for the space it has been given. Pure — pinned by backgammon-fit.test.ts. */
export function boardGeometry(availW: number, availH: number): BoardGeometry {
  const capW = Math.max(160, Math.round(availW));
  const capH = Math.max(150, Math.round(availH) - FIT_SAFETY_PX);

  const solve = (boardW: number): BoardGeometry => {
    const feltW = boardW - RIM_X * 2;
    // 12 points + the spine, which is itself SPINE_RATIO points wide — so the checker that sits on
    // the bar is always narrower than the channel it sits in.
    const pointW = feltW / (12 + SPINE_RATIO);
    const availFeltH = capH - RAIL_H * 2;
    // What the points would like: the reference's ≈39%-of-felt each, clamped to a real proportion.
    const ideal = (availFeltH * (1 - BAND_FRACTION)) / 2;
    const pointH = Math.max(pointW * MIN_POINT_RATIO, Math.min(pointW * MAX_POINT_RATIO, ideal));
    // The band takes the surplus, but never less than a die needs nor more than a third of the felt.
    const bandH = Math.max(
      BAND_MIN_PX,
      Math.min((2 * pointH * BAND_MAX_FRACTION) / (1 - BAND_MAX_FRACTION), availFeltH - 2 * pointH),
    );
    // Round the PARTS first and derive the whole from them. The band is an overlay positioned at
    // `top: pointH` with `height: bandH` inside a `feltH` box, so if these three did not add up
    // exactly the band would sit a pixel off its own strip.
    //
    // The clamp is re-applied to the ROUNDED length (floor on the way up, ceil on the way down):
    // rounding a length against a fractional point WIDTH would otherwise push the ratio a hair
    // outside the very band this function promises to hold.
    const pH = Math.min(
      Math.floor(pointW * MAX_POINT_RATIO),
      Math.max(Math.ceil(pointW * MIN_POINT_RATIO), Math.round(pointH)),
    );
    const bH = Math.round(bandH);
    const feltH = 2 * pH + bH;
    return {
      w: Math.round(boardW),
      h: feltH + RAIL_H * 2,
      feltW: Math.round(feltW),
      feltH,
      pointW,
      spineW: Math.round(pointW * SPINE_RATIO),
      pointH: pH,
      bandH: bH,
      ratio: pH / pointW,
    };
  };

  let g = solve(capW);
  // HEIGHT-BOUND (Telegram Desktop is wide and SHORT): give WIDTH back so the points keep their
  // proportion, rather than flattening them — the defect this function exists to remove.
  if (g.h > capH) {
    const feltBudget = capH - RAIL_H * 2 - BAND_MIN_PX;
    const pointH = Math.max(24, feltBudget / 2);
    const pointW = pointH / MIN_POINT_RATIO;
    const boardW = Math.min(capW, pointW * (12 + SPINE_RATIO) + RIM_X * 2);
    g = solve(boardW);
    // Solving at the narrower width can still land a pixel or two over; clamp the band, not the points.
    if (g.h > capH) {
      const bandH = Math.max(40, capH - RAIL_H * 2 - 2 * g.pointH);
      const feltH = 2 * g.pointH + bandH;
      g = { ...g, bandH, feltH, h: feltH + RAIL_H * 2 };
    }
  }
  return g;
}

/** The point ratio a solved board produces. Kept as a named helper so tests read intently. */
export function pointRatioOf(g: BoardGeometry): number {
  return g.ratio;
}

/**
 * The PER-TURN deadline — تخته نرد's ONE clock.
 *
 * ⚠️ There used to be a second one: a chess-style whole-game time bank with its own chip on each
 * panel. It was removed outright (operator, 2026-08-15) — a backgammon turn is a throw the player
 * did not choose plus a couple of taps, so a whole-game budget measures the dice as much as the
 * thinking. This window is also what the idle handover counts. Anchored to a SERVER epoch, so a
 * slow phone shows the same number as a fast one.
 */
export interface BgTurnTimer {
  /** Server epoch (ms) at which the current turn expires. */
  endsAt: number;
  /** Whose deadline it is. */
  seat: number;
  /** The table's per-turn budget in seconds — the FALLBACK for how full the ring starts. */
  totalSec: number;
  /**
   * How long THIS window actually is (ms), straight off the server's `turn` frame.
   *
   * ⚠️ NOT the same thing as `totalSec × 1000`, and assuming it was is what made the ring look
   * FROZEN on a double: the server armed a longer window than the table's configured length, so
   * the ring clamped to the setting and sat pinned at full until the remaining time fell back
   * under it. Optional only for version skew — a bundle newer than its game-server falls back.
   */
  totalMs?: number;
}

/** A player's identity for a strip — the shared name parts plus the avatar the strip renders.
 *  A structural superset of the online roster identity, so a `{ nickname, avatarId, avatarPhotoId }`
 *  from the container is assignable. Omit it (solo tests) → a preset avatar + «شما»/«حریف». */
export interface BgIdentity extends DisplayNameParts {
  avatarId?: string | null;
  avatarPhotoId?: string | null;
  /** Official/verified account → the disc wears the badge. */
  isOfficial?: boolean;
}

/**
 * The 6·BAR·6 display columns for each viewer. Standard orientation: the viewer sits at the bottom,
 * home board bottom-RIGHT (nearest the tray). Seat 0's home = points 0..5; seat 1's = 18..23. The
 * board FLIPS per viewer so each sees their own home bottom-right. (Pixel geometry — triangle tips,
 * the central well — is Phase-4 CSS; this returns the ABSOLUTE point order per row for both taps + render.)
 */
export function orientRows(you: number): { top: number[]; bottom: number[] } {
  if (you === 0) {
    return {
      // bottom-left outer (11..6) | BAR | bottom-right home (5..0)
      bottom: [11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
      // top-left (12..17) | BAR | top-right opponent home (18..23)
      top: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
    };
  }
  // Seat 1: mirror so 18..23 sits bottom-right.
  return {
    bottom: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
    top: [11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
  };
}

/**
 * ITEM 12 — which checker look the LOCAL player wears. Gold unless they chose obsidian.
 *
 * A context rather than a prop because `Chip` is reached through Stack → Point → the tray and
 * the bar; threading a boolean down all of those to flip two gradients would touch a dozen
 * signatures for one viewer-side preference. Read in exactly one place, right where the colour
 * is painted. Purely visual: nothing about the position, the engine or the wire changes, so two
 * players who picked the same colour still see a consistent board — each sees their OWN
 * checkers in their own choice, the opponent's in the other.
 */
export const BackgammonColorContext = createContext<BackgammonCheckerColor>("gold");

/** A single premium chip. `mine` = the local player's colour, opponent = the other one. The
 *  outermost chip of a >5 stack is stamped. */
function Chip({ mine, stamp }: { mine: boolean; stamp?: number }): ReactNode {
  // `gold` is what gets PAINTED; `mine` is only whose checker it is. They coincide until the
  // player picks obsidian, at which point every pair simply swaps.
  const gold = useContext(BackgammonColorContext) === "gold" ? mine : !mine;
  return (
    <span
      className={mine ? "bg-checker bg-checker--you" : "bg-checker bg-checker--opp"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "var(--pt-w, 24px)",
        aspectRatio: "1",
        borderRadius: "50%",
        /* A real chip, not a flat token (the reference's checkers are glossy discs with a raised
           centre): the body gradient, a turned RIM, a recessed inner disc, and ONE specular
           highlight. All of it is background + box-shadow, so a stack of fifteen still costs one
           paint each and nothing here can shift layout. */
        background: gold
          ? "radial-gradient(circle at 32% 28%, #ffeaa3 0%, #e0b64a 42%, #a5820e 74%, #5c4802 100%)"
          : "radial-gradient(circle at 32% 28%, #4a5158 0%, #24292e 45%, #0d1114 78%, #000000 100%)",
        border: gold ? "1px solid #7a5e0b" : "1px solid #3a4048",
        boxShadow: gold
          ? [
              "inset 0 0 0 2px rgba(255,247,214,0.5)", // turned rim
              "inset 0 0 0 5px rgba(120,92,8,0.35)", // recessed step
              "inset 0 3px 5px rgba(255,255,255,0.45)", // specular
              "inset 0 -3px 6px rgba(0,0,0,0.35)",
              "0 3px 6px rgba(0,0,0,0.5)",
            ].join(",")
          : [
              "inset 0 0 0 2px rgba(190,205,220,0.22)",
              "inset 0 0 0 5px rgba(0,0,0,0.5)",
              "inset 0 3px 5px rgba(255,255,255,0.22)",
              "inset 0 -3px 6px rgba(0,0,0,0.5)",
              "0 3px 6px rgba(0,0,0,0.55)",
            ].join(","),
        fontWeight: 800,
        fontSize: "0.7em",
        lineHeight: 1,
        color: gold ? "rgb(var(--c-ink-on-gold))" : "rgb(var(--c-card-face))",
      }}
    >
      {stamp ? faNum(stamp) : ""}
    </span>
  );
}

/** A stack of chips at a point (up to 5 real chips; a >5 stack shows 4 + a stamped top chip).
 *  Chips are seated from the point's BASE toward its apex by the parent's flex direction, so a stack
 *  that grows always grows at the apex end — the arriving checker is drawn by {@link FlyingChecker},
 *  a board-level overlay, not by marking one chip in here. (Marking a chip meant the "new" checker
 *  appeared at whichever DOM end happened to be last, which on the bottom row is the BOTTOM of the
 *  pile — the operator's "a new checker must be added from the TOP of the stack".) */
function Stack({
  count,
  mine,
  row,
}: {
  count: number;
  mine: boolean;
  row: "top" | "bottom";
}): ReactNode {
  if (count === 0) return null;
  const shown = count > 5 ? 4 : count;
  const chips: ReactNode[] = [];
  for (let k = 0; k < shown; k += 1) chips.push(<Chip key={k} mine={mine} />);
  // No rule caps a point: a player may legally stack all fifteen of their own checkers on one point
  // (a point only BLOCKS the opponent once it holds two). So >5 collapses to 4 chips + a stamped
  // count rather than truncating — the display scales to any count.
  if (count > 5) chips.push(<Chip key="stamp" mine={mine} stamp={count} />);
  return (
    <span
      style={{
        display: "flex",
        // Grow from the point's BASE toward its apex, so a new checker joins at the TOP of the pile
        // and the checkers already there stay put. A plain `column` grew a bottom-row pile at the
        // BASE (the operator's "a new checker must be added from the TOP, not the bottom") — the
        // bottom row apex points UP, so its pile must build upward (column-reverse).
        flexDirection: row === "top" ? "column" : "column-reverse",
        alignItems: "center",
        gap: CHIP_GAP,
      }}
    >
      {chips}
    </span>
  );
}

/**
 * The travelling checker: a board-level overlay that flies from the move's SOURCE to its
 * DESTINATION and then vanishes, leaving the (already updated) static stacks to show the result.
 *
 * This replaces the old "mark one chip in the destination stack and slide it in" trick, which could
 * only animate point→point moves — a bar entry or a bear-off has no chip in a destination stack, so
 * those two moves had NO motion at all. Flying an independent chip animates every move kind
 * uniformly, and lands ON TOP of whatever it passes.
 */
/**
 * Which slot of a stack a flight's endpoint sits in.
 *
 * `count` is the stack size in the PACED view, which already has every applied sub-move — so the
 * checker arriving NOW is always the top one, and the one that just left was standing above what
 * remains.
 *
 * ⚠️ IT USED TO SUBTRACT WHATEVER WAS STILL IN THE AIR toward the same spot, which collapsed two
 * concurrent arrivals onto ONE slot: the first flight was created when the view held 1 checker
 * (index 0) and the second when it held 2 but one was airborne (2−1−1 = 0 again). Both landed on
 * the same pixel. Each applied sub-move already raised `count`, so the position is simply the top.
 */
export function flightSlot(count: number, end: "from" | "to"): number {
  return end === "to" ? count - 1 : count;
}

function FlyingChecker({
  from,
  to,
  mine,
  size,
  knock,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  mine: boolean;
  size: number;
  /** The blot this walk knocks to the bar (§5.2's other half). Before this existed a hit had a
   *  SOUND and no picture: the struck checker simply teleported onto the bar. */
  knock?: { from: { x: number; y: number }; to: { x: number; y: number } } | null;
}): ReactNode {
  return (
    <>
      <SlideChecker
        dx={from.x - to.x}
        dy={from.y - to.y}
        animate
        style={
          {
            position: "fixed",
            left: to.x - size / 2,
            top: to.y - size / 2,
            width: size,
            height: size,
            zIndex: 60,
            pointerEvents: "none",
            display: "block",
            // ⚠️ `--pt-w` is published on `.bg-board`, and this overlay is a FIXED sibling OUTSIDE
            // it — so the variable did not resolve here and every travelling checker fell back to
            // 24px while the static ones measured 27px. It visibly shrank on take-off and grew on
            // landing. Re-publish the measured size on the flight itself.
            "--pt-w": `${size}px`,
          } as CSSProperties
        }
      >
        <Chip mine={mine} />
      </SlideChecker>
      {knock ? (
        <SlideChecker
          dx={knock.from.x - knock.to.x}
          dy={knock.from.y - knock.to.y}
          animate
          className="bg-knock"
          style={
            {
              position: "fixed",
              left: knock.to.x - size / 2,
              top: knock.to.y - size / 2,
              width: size,
              height: size,
              // Above the arriving checker: the blot is what just got hit, so it reads on top.
              zIndex: 61,
              pointerEvents: "none",
              display: "block",
              "--pt-w": `${size}px`,
            } as CSSProperties
          }
        >
          {/* The struck blot belongs to the OTHER side from whoever moved. */}
          <Chip mine={!mine} />
        </SlideChecker>
      ) : null}
    </>
  );
}

/** Base <button> reset (Tailwind preflight is off → reset explicitly). Identical to every other
 *  table — do NOT add `border-0 bg-transparent p-0` here: those beat a button's own border/bg
 *  utilities in the cascade and flatten the exit chip to a bare icon. */
const BTN = "appearance-none m-0 cursor-pointer";

/*
 * THE §5.1 OPENING ROLL-OFF — no longer a component.
 *
 * It used to be a separate overlay that MOUNTED TWO FRESH DICE with `throwing`, at the very same
 * coordinates the contest slots had just used. So the instant the second player threw, the board
 * threw BOTH dice again — the reported «my die is thrown twice», measured as my own die animating
 * at t=927ms and again at t=1952ms — and the playing tray then threw the same pair a THIRD time
 * when the 3s ceremony ended.
 *
 * The ceremony is now a CAPTION under dice that are already at rest (see the band): the winner's
 * die is named, the loser's recedes, and nothing re-mounts. `OPENING_MS` still governs how long
 * that caption holds, in lockstep with the engine's `OPENING_ANNOUNCE_MS` pacing hint.
 */

/** Re-render on a 1s beat, but only while it is actually needed (a stopped ring costs nothing). */
function useSecondTick(active: boolean): void {
  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => force((n) => n + 1), 250);
    return () => window.clearInterval(id);
  }, [active]);
}

/**
 * The per-turn countdown ring on a player's panel (operator's call, 2026-08-09).
 *
 * ⚠️ NOT a chess clock: `endsAt` is the deadline for THIS TURN, broadcast by the server, so the ring
 * is anchored to a server epoch and cannot drift. The idle player's ring is drawn dim and empty —
 * exactly as the reference does — so the two panels always read as one active / one waiting.
 */
function TurnRing({
  endsAt,
  totalSec,
  totalMs,
  active,
  grantedSec = null,
  size = TURN_RING_PX,
}: {
  endsAt: number | null;
  totalSec: number;
  totalMs?: number;
  active: boolean;
  /** Seconds a DOUBLE has just added to this turn — drawn as a «+۱۰ ثانیه» chip for a beat. */
  grantedSec?: number | null;
  size?: number;
}): ReactNode {
  useSecondTick(active && endsAt !== null);
  const leftMs = endsAt !== null ? Math.max(0, endsAt - Date.now()) : 0;
  /*
   * DRAW THE WINDOW THE SERVER ARMED, NOT THE ONE THE TABLE IS CONFIGURED FOR.
   *
   * Both readings below clamp — the digits so a few ms of clock skew cannot make a 20s turn read
   * «۲۱», the arc so it never overfills. Clamping against the CONFIGURED length was the bug: with a
   * window longer than the setting the ring pinned at «۲۰» on a full arc for the whole overshoot
   * and looked like it had stopped. `totalMs` is the armed window itself.
   *
   * ⚠️ A تخته نرد DOUBLE STRETCHES THE TURN TO 1.5× THE SETTING, so this is not a corner case — it
   * is every sixth turn. The `totalSec` fallback exists only for version skew (a bundle older than
   * its server), and on a double that fallback IS the frozen ring — the exact defect that made this
   * rule look broken the first time it shipped. Web and game-server must go out together.
   */
  const windowMs = totalMs ?? totalSec * 1000;
  const secs = Math.min(Math.ceil(windowMs / 1000), Math.ceil(leftMs / 1000));
  const frac = active && windowMs > 0 ? Math.max(0, Math.min(1, leftMs / windowMs)) : 0;
  const r = size / 2 - 2.5;
  const c = 2 * Math.PI * r;
  const urgent = active && secs <= 10;
  return (
    <span
      data-testid="bg-turn-ring"
      data-active={active ? "1" : "0"}
      style={{ position: "relative", width: size, height: size, flex: "none", display: "block" }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={2.25}
          stroke="rgb(var(--c-outline-variant))"
          opacity={active ? 0.45 : 0.3}
        />
        {active ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={2.25}
            strokeLinecap="round"
            stroke={urgent ? "rgb(var(--c-error))" : "rgb(var(--c-secondary))"}
            strokeDasharray={c}
            strokeDashoffset={c * (1 - frac)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        ) : null}
      </svg>
      <span
        data-testid="bg-turn-secs"
        className="font-numeral tabular-nums"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: active
            ? urgent
              ? "rgb(var(--c-error))"
              : "rgb(var(--c-secondary))"
            : "rgb(var(--c-on-surface-variant))",
        }}
      >
        {faNum(active ? secs : 0)}
      </span>
      {/*
        «+۱۰ ثانیه» — the double's extra time, NAMED.
        Without it the ring simply grows and reads as a stutter; with it the player is told what
        they were given, in the same beat as the ×N badge that says why.

        ⚠️ ABSOLUTELY POSITIONED INSIDE THE RING'S OWN BOX, so it cannot move a name, a strike dot
        or a panel — the same discipline `Avatar`'s official badge had to learn. It sits over the
        «نوبت شماست» line below the ring (an OPAQUE pill, so it covers rather than collides) because
        that line is INSIDE the panel: anything above the ring would push against the board's own
        `overflow: hidden` on the opponent's strip.
      */}
      {grantedSec !== null && grantedSec > 0 ? (
        <span
          data-testid="bg-time-granted"
          className="font-numeral tabular-nums"
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 1,
            padding: "0 5px",
            borderRadius: 999,
            background: "rgb(var(--c-secondary))",
            color: "rgb(var(--c-ink-on-gold))",
            fontSize: 9,
            lineHeight: "13px",
            fontWeight: 800,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {fa.backgammon.timeGranted(faNum(grantedSec))}
        </span>
      ) : null}
    </span>
  );
}

/**
 * How many expired turns cost a seat its place — the client's copy of the server's
 * `BACKGAMMON_IDLE_MISS_LIMIT`. Only the DOT COUNT is drawn from it; the rule itself lives on the
 * server, so a stale bundle mis-draws the dots at worst, never the outcome.
 */
const STRIKE_LIMIT = 5;

/** «اخطار ۰/۵» — how many turns this seat has let expire. Five and the bot plays the seat. */
function StrikeDots({ used, of }: { used: number; of: number }): ReactNode {
  return (
    <span
      data-testid="bg-strikes"
      className="flex flex-none items-center"
      // The WORD «اخطار» is carried by the accessible name only: spelled out, it plus the pip and
      // the clock overflowed the panel on a 390px phone and the dots — the part that actually says
      // how close you are — were the first thing clipped.
      aria-label={`${fa.backgammon.strikes} ${faNum(used)}/${faNum(of)}`}
      title={fa.backgammon.strikes}
    >
      <span className="flex gap-0.5">
        {Array.from({ length: of }, (_, i) => (
          <span
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background:
                i < used ? "rgb(var(--c-error))" : "rgb(var(--c-on-surface-variant) / 0.35)",
            }}
          />
        ))}
      </span>
    </span>
  );
}

/**
 * THE MATCH SCOREBOARD — one box, and every number in it says what it is.
 *
 * The score used to sit INSIDE EACH PANEL as «۰ از ۵» and the round number in a separate header
 * chip: three places to look for one fact, and at nil-nil the two panel chips read identically. That
 * was collapsed into one box (حکم's «ما | حریف» shape) — but the box then carried four bare numbers
 * («۲ | ۱» over «دست ۱ · تا ۵») with nothing naming any of them, which is the «still scattered»
 * report: a reader had to already know that the gold one was theirs and that «تا ۵» counted دست.
 *
 * So each half is LABELLED («تو» / «حریف») and the caption is a sentence («دست ۱ از ۵»). Same box,
 * same 40px, nothing new on screen — every number now carries its own meaning.
 *
 * A single دست (`matchTo === 1`) is not a match, so there is nothing to report and nothing is drawn.
 */
function BgScoreBox({
  mine,
  theirs,
  round,
  target,
}: {
  mine: number;
  theirs: number;
  round: number;
  target: number;
}): ReactNode {
  return (
    <span
      data-testid="bg-score"
      aria-label={fa.backgammon.scoreAria(faNum(mine), faNum(theirs), faNum(round), faNum(target))}
      className="flex flex-none flex-col items-center justify-center rounded-lg border border-outline-variant/70 bg-surface-container/70 px-2"
      /* EXACTLY the exit button's height. The header row is `items-center`, so a box even one
         pixel taller re-centres the buttons and knocks them out of line with the shell's fixed
         chat toggle — the very misalignment this pass is fixing (measured: 41px → 1px of drift). */
      style={{ height: 40 }}
    >
      <span aria-hidden className="flex items-center gap-1 leading-none">
        <span className="text-secondary/80" style={{ fontSize: 8.5, lineHeight: "10px" }}>
          {fa.backgammon.you}
        </span>
        <span className="font-numeral text-body-lg font-bold tabular-nums text-secondary">
          {faNum(mine)}
        </span>
        <span className="mx-0.5 h-3.5 w-px flex-none bg-outline-variant" />
        <span className="font-numeral text-body-lg font-bold tabular-nums text-on-surface-variant">
          {faNum(theirs)}
        </span>
        <span className="text-on-surface-variant/80" style={{ fontSize: 8.5, lineHeight: "10px" }}>
          {fa.backgammon.opponent}
        </span>
      </span>
      <span
        aria-hidden
        className="mt-0.5 font-caption tabular-nums text-on-surface-variant"
        style={{ fontSize: 9.5, lineHeight: "11px", whiteSpace: "nowrap" }}
      >
        {/* ⚠️ «دست ۶ از ۵» IS REACHABLE — a دست pays 1, 2 or 3, so alternating single wins run a
            «۵ دست» match to as many as nine دست. Past the target the caption drops the «از N» half
            rather than printing a contradiction. */}
        {round > target
          ? fa.backgammon.scoreCaptionOver(faNum(round))
          : fa.backgammon.scoreCaption(faNum(round), faNum(target))}
      </span>
    </span>
  );
}

/**
 * A player panel. The ACTIVE one wears the gold inset ring and states the turn IN WORDS — a ring on
 * its own never told anyone whose ring it was, which is the whole reason the reference prints
 * «My Turn» under it.
 */
function BgPanel({
  mine,
  parts,
  active,
  ring,
  strikes,
  handover,
}: {
  mine: boolean;
  parts?: BgIdentity | null;
  active: boolean;
  ring: ReactNode;
  strikes: ReactNode;
  handover: boolean;
}): ReactNode {
  return (
    <div
      data-testid={mine ? "bg-panel-you" : "bg-panel-opp"}
      data-active={active ? "1" : "0"}
      className={`flex items-center gap-2 rounded-xl border border-outline-variant/70 bg-surface-container/70 px-2.5${
        active ? " bg-panel--active" : ""
      }${handover ? " bg-handover" : ""}`}
      style={{ height: PANEL_H, flex: "none" }}
    >
      <Avatar
        avatarId={parts?.avatarId ?? (mine ? "fox" : "wolf")}
        avatarPhotoId={parts?.avatarPhotoId}
        size={34}
        official={parts?.isOfficial === true}
        ringClass={active ? "border-secondary" : "border-outline-variant"}
      />
      {/* ONE LINE, not two. The panel used to stack the name over a details row (pip · match ·
          clock · strikes), which is what made it 62px tall and pushed the whole board down onto
          the home indicator. With the pip AND the time bank gone the rest fits beside the name. */}
      <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
        <span className="min-w-0 truncate text-body-md font-bold text-on-surface">
          {parts ? displayName(parts) : mine ? fa.backgammon.you : fa.backgammon.opponent}
        </span>
        {/* THE VERIFIED BADGE, BESIDE THE NAME — where every other surface in the app puts it
            (profile, chat header, friends rows, the table-chat roster). It was stamped on the 34px
            avatar disc only, which renders a 12px tick nobody could see; an official account now
            reads the same here as it does everywhere else. `flex-none` so a long name truncates
            around it rather than pushing it out. */}
        {parts?.isOfficial === true ? <OfficialBadge size={13} /> : null}
        {strikes}
      </span>
      <span
        className="flex flex-none flex-col items-center justify-center gap-0.5"
        style={{ width: TURN_COL_W }}
      >
        {ring}
        {/* ⚠️ `visibility`, not `color: transparent`. Both labels are always in the DOM so the
            column never changes width — but a transparent one is still READ OUT, so a screen
            reader announced «نوبت شماست» and «نوبت حریف» back to back every single turn.
            `visibility: hidden` keeps the reserved box and leaves the accessibility tree. */}
        <span
          data-testid={mine ? "bg-turn-you" : "bg-turn-opp"}
          className="font-bold"
          style={{
            color: "rgb(var(--c-secondary))",
            visibility: active ? "visible" : "hidden",
            fontSize: 9.5,
            lineHeight: "11px",
            whiteSpace: "nowrap",
          }}
        >
          {mine ? fa.backgammon.turnYours : fa.backgammon.turnTheirs}
        </span>
      </span>
    </div>
  );
}

/**
 * A card in the MIDFIELD BAND — the only place a message may appear over the felt, so it can never
 * cover a checker, a point or the spine.
 *
 * ⚠️ IT MUST FIT THE BAND, and on a short viewport it did not. Measured with the entrance animation
 * disabled: at 320×568 the bar hint stood 93px inside a 68px band — 13px spilling onto the points
 * above AND below — and 68 in a 62px band on Telegram Desktop. The cause is width, not padding: the
 * message half is 44% of the felt (131px at 320), which wraps a Persian hint to three lines. So the
 * card TIGHTENS on a short band (smaller type, less padding) and is hard-capped to the band's own
 * height, which is what makes «a toast can never leave the band» a property rather than a hope.
 */
const TIGHT_BAND_PX = 80;

/**
 * card-98 6.5 — the type steps a band toast that is asked to FIT may take, largest first. The roll
 * hint («نوبت توئه — «تاس بریز» رو بزن 🎲») was cut off on the operator's Android at text zoom ×1.3
 * and ×1.5 and on Telegram Desktop 307×472 (an 82 px message slot): the cap above held it inside
 * the band, and its end went. It now steps down only as far as it must; where it fits it is untouched.
 */
export const FIT_SCALES: readonly number[] = [1, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6];

/** The largest step at which the content fits (`overflowsAt` false); the smallest if none does. */
export function fitScale(overflowsAt: (scale: number) => boolean): number {
  for (const scale of FIT_SCALES) if (!overflowsAt(scale)) return scale;
  return FIT_SCALES[FIT_SCALES.length - 1]!;
}

function BandToast({
  tone,
  testId,
  bandH,
  fit = false,
  children,
}: {
  tone: "info" | "warn" | "stop";
  testId: string;
  /** The band's measured height — the box this card may not leave. */
  bandH: number;
  /** card-98 6.5: step the type down until the message fits the band (see {@link FIT_SCALES}). */
  fit?: boolean;
  children: ReactNode;
}): ReactNode {
  const tight = bandH < TIGHT_BAND_PX;
  const boxRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  // Re-fit once the web font is in: a fallback face wraps differently.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    if (!fit || typeof document === "undefined" || !document.fonts) return undefined;
    let live = true;
    void document.fonts.ready.then(() => {
      if (live) setFontsReady(true);
    });
    return () => {
      live = false;
    };
  }, [fit]);
  /*
   * Measured, never animated: once per band size (and once the font is in), synchronously before
   * paint. `zoom` — not `transform` — because it shrinks the LAYOUT box too, so the overflow it is
   * measuring really goes away; it scales whatever the text zoom made of the type.
   */
  useLayoutEffect(() => {
    const box = boxRef.current;
    const inner = innerRef.current;
    if (!fit || !box || !inner) return;
    const overflows = (): boolean =>
      box.scrollHeight > box.clientHeight + 1 || box.scrollWidth > box.clientWidth + 1;
    const apply = (scale: number): void => {
      inner.style.zoom = scale === 1 ? "" : String(scale);
    };
    apply(
      fitScale((scale) => {
        apply(scale);
        return overflows();
      }),
    );
  }, [fit, bandH, tight, fontsReady]);
  return (
    <div
      ref={boxRef}
      data-testid={testId}
      role="status"
      className={`bg-band-toast text-center${tight ? " bg-band-toast--tight px-2 py-1" : " px-3 py-2"}${
        tone === "warn" ? " bg-band-toast--warn" : tone === "stop" ? " bg-band-toast--stop" : ""
      }`}
      style={{
        maxWidth: "94%",
        // The hard cap. `overflow: hidden` is the belt, not the fix — the tightening above is what
        // keeps a real message inside it.
        //
        // ⚠️ 2px, NOT 6. Measured in Chromium across 11 viewports: at Telegram Desktop 440×640 the
        // band is 62px, so a 6px inset left 54px — and TWO LINES of tight type need 58px. That made
        // EVERY two-line toast clip there, whatever it said, down to nine characters. At 2px the cap
        // is 58px and the same toast fits, while still measuring INSIDE the band on every viewport.
        maxHeight: Math.max(24, bandH - 2),
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {fit ? <div ref={innerRef}>{children}</div> : children}
    </div>
  );
}

/**
 * WHERE A CONTEST DIE SITS — its centre, as a fraction of the felt's width from its OWN edge.
 * 21% (the old `width:40%` slot) read as "out in the corner"; a quarter of the way in is clearly
 * inside its own half without reaching the message. Floored at one point's width from the rail.
 */
const CONTEST_DIE_CENTRE = 0.25;
/** …and how wide the message between them may be while both dice are on the felt. */
const CONTEST_TOAST_MAX = "36%";

/**
 * The longest the board will withhold a roll's consequences, measured from the moment the hold
 * began — a ceiling on the reveal gate, not its normal length (that is `DIE_THROW_MS`).
 *
 * The gate is armed by two independent signals (the dice frame and the window frame) which arrive
 * as separate socket messages. This is what guarantees that a stream of them can never hold the
 * badge, the hint and the ring shut for longer than one throw plus the gap between two frames —
 * i.e. that the fix for a frozen-looking ring can never itself freeze the board.
 */
const REVEAL_CAP_MS = DIE_THROW_MS + 600;

/**
 * How long the ring takes to EASE onto a stretched window, and how long the «+۱۰ ثانیه» chip that
 * names it stays up.
 *
 * A double adds time mid-turn, so the remaining time goes up — unavoidably. Snapping reads as a
 * glitch (measured on the real controller: digits 7 → 17, arc 34% → 55% in one frame); an eased
 * grow with a label reads as a grant. The chip outlasts the ease so the number is still on screen
 * once the ring has settled on it.
 */
const RING_GROW_MS = 600;
const GRANT_CHIP_MS = 1200;

export function BackgammonTable({
  // The AUTHORITATIVE view straight off the wire. Deliberately shadowed below by the PACED view the
  // board actually renders — see `useBgReplay`. Nothing in this component should read this one.
  view: authoritative,
  legal,
  onMove,
  onLeave,
  self,
  opponent,
  timer,
  strikes,
  frozen: frozenProp,
  onDraining,
}: {
  view: BgView;
  // `readonly` mirrors ChessTable + the solo store's `legal: readonly BgMove[]`, so the
  // container can pass the store list straight through (the table only READS it via `.some`).
  legal: readonly BgMove[];
  onMove: (move: BgMove) => void;
  onLeave: () => void;
  /** The signed-in user's identity (real profile ONLINE / preset SOLO). Omitted → preset + «شما». */
  self?: BgIdentity | null;
  /** The opponent's identity (real profile ONLINE / bot SOLO). Omitted → preset + «حریف». */
  opponent?: BgIdentity | null;
  /** The PER-TURN deadline (operator's call, 2026-08-09) — server epoch, so it cannot drift.
   *  Omitted / null → no ring is drawn and the panel simply states the turn in words, which is what
   *  a solo game and any pre-timer server both get. */
  timer?: BgTurnTimer | null;
  /** Expired turns per seat, indexed by seat. Two and the bot plays that seat (the seat stays
   *  reclaimable — backgammon is 2-player, so locking the human out is effectively a loss). */
  strikes?: readonly [number, number] | null;
  /** TEST SEAM — points whose checkers are §5.2-frozen. Normally derived from the replay events. */
  frozen?: readonly number[];
  /** Told whenever the replay starts/stops draining, so a container can hold its end overlay until
   *  the last checker has actually been seen to move. */
  onDraining?: (draining: boolean) => void;
}): ReactNode {
  /*
   * THE PACED VIEW.
   *
   * The board renders a replay of the authoritative view rather than the view itself. Frames arrive
   * as fast as the mover taps, so rendering them directly is exactly why a real opponent's checkers
   * teleported — and the old single flight slot cancelled its own predecessor mid-air. `view` below
   * is the PACED projection; every existing `view.…` read therefore becomes replay-driven for free.
   */
  const replay = useBgReplay(authoritative);
  const view = replay.view;

  // Game sound cues (dice / place / hit / bear-off / your-turn). Wired ONCE here → covers solo +
  // online; every cue adopts-at-mount silently so a resume/reconnect never replays. Fed the PACED
  // view, so a cue can never sound before the motion it belongs to.
  useBackgammonSfx(view);

  // Let a container hold its end overlay until the board has finished showing the final checker.
  useEffect(() => {
    onDraining?.(replay.draining);
  }, [replay.draining, onDraining]);

  // A selected source spot (tap-to-move); null between turns / after a move lands.
  const [selected, setSelected] = useState<BgSpot | null>(null);
  // The illegal-attempt shake (§12 A10) — the selected source jitters briefly, then clears (mirror
  // ChessTable's `shakeSquare`). Recomputed each render, and only armed when motion is allowed, so a
  // fill-mode beat never strands.
  const [shakeSpot, setShakeSpot] = useState<BgSpot | null>(null);
  /** A transient message in the midfield band (a tap on someone else's turn). */
  const [nudge, setNudge] = useState<string | null>(null);
  const reduced = prefersReducedMotion();

  // Backgammon is strictly 2-player → narrow the Seat (a bare `number`) to 0|1 ONCE, so every
  // tuple read below (`bar`/`off`/`pip` indexed by seat) is exact (no spurious `| undefined`).
  const you: 0 | 1 = view.you === 0 ? 0 : 1;
  const oppSeat: 0 | 1 = you === 0 ? 1 : 0;

  const isMyTurn = view.turn === view.you && view.status === "playing";
  const canRoll = legal.some((m) => m.kind === "roll");

  // A fresh roll (new dice, nothing staged yet) bumps this; the dice are keyed by it, so each roll
  // remounts them and replays the throw exactly once. `rollNo` also seeds the per-die variance.
  const rollKey = view.dice.join(",");
  const stagedLen = view.staged.length;
  const rollNo = useRef(0);
  // ADOPT-AT-MOUNT: seed the previous roll from the FIRST view, so mounting a table that already
  // has dice on it (a resume, a reconnect, or simply re-entering) does not replay a throw for dice
  // that were rolled long ago. Only a roll that happens while we are watching is thrown.
  const prevRollKey = useRef<string | null>(null);
  const thrown = useRef(false);
  /*
   * §5.1 — THE TWO HALVES BELONG TO THE CONTEST, AND TO NOTHING ELSE.
   *
   * The contest is two people each throwing ONE die into their own half, so it is drawn that way
   * while it runs and through the announcement that resolves it. The pair then GATHERS INTO THE
   * ROLLER'S OWN TRAY like every other roll — «both dice must sit on the roller's own right-hand
   * side» (operator, 2026-08-15).
   *
   * ⚠️ THIS REPLACES A LATCH THAT LIED. The pair used to be HELD in the two halves for «the whole
   * first turn», tested as `opening !== undefined && lastMove.length === 0` and re-evaluated at
   * every roll boundary. `opening` stays set for the whole دست and `lastTurn` is refilled on every
   * confirm — but a DANCE confirms nothing, so `lastTurn` went back to [] and the latch re-engaged
   * MID-ROUND: from then on the felt drew the round's contest pair while a completely different
   * roll was in play, drew no tray dice at all, and still drew the ×N badge off the live roll.
   * Measured on the deployed engine over 200 games: 5.13% of all rolls displayed stale dice, 0.83%
   * with the bare badge beside them. That is the reported «3 and 1 with a 4 next to it, playing as
   * a double». The old comment called the case «unreachable from the standard start position» — true
   * of the FIRST turn only, which is the one turn it checked.
   *
   * Both surviving conditions are ONE-SHOT and cannot recur: a LIVE `view.openingRoll`, and the
   * replay's own `openingRollOff` event (time-boxed by `OPENING_MS`).
   */
  const inContest = view.openingRoll !== undefined;
  /** The previous render was still IN the contest — so this roll is the pair it just decided. */
  const cameFromContest = useRef(false);

  if (prevRollKey.current === null) {
    prevRollKey.current = rollKey;
  } else if (rollKey && rollKey !== prevRollKey.current && stagedLen === 0) {
    rollNo.current += 1;
    /*
     * THE §5.1 PAIR MUST NOT RE-THROW WHEN IT GATHERS INTO THE TRAY. Each die was already thrown,
     * one at a time, in its own contest slot; replaying it here is the «one die thrown three times»
     * defect that the hold was originally introduced to cure. Every OTHER roll throws normally.
     */
    thrown.current = !cameFromContest.current;
  }
  prevRollKey.current = rollKey;
  cameFromContest.current = inContest;

  /*
   * NOTHING THE ROLL DECIDES IS SHOWN UNTIL THE DICE HAVE SETTLED (operator, 2026-08-16).
   *
   * «The ×۴ badge and the timer both react BEFORE the dice have finished landing — so the game
   * reveals it's a double before I can read it myself.» Both are true, and they land in the same
   * instant: the server's `state` frame carries the dice VALUES and its `turn` frame carries the
   * window the roll just opened, while the cubes are still tumbling for `DIE_THROW_MS`. So the
   * board announced «×۴», said «حرکت با ۴ (۴ بار)» in words, and reset its ring — a whole second
   * before the throw it was describing had finished. It reads as a scripted result, not a throw.
   *
   * ONE gate for all three, so they can never disagree with each other or with the dice. Three
   * things about it are load-bearing:
   *
   *  • COMPUTED DURING RENDER, never set from an effect. An effect runs AFTER paint, so the badge
   *    and the 2× ring would each paint for one frame before the gate closed — precisely the frame
   *    being hidden. (The contest's own `contestLanded` can afford an effect: it only DELAYS a
   *    caption, it does not hide a spoiler.)
   *  • ARMED FROM BOTH SIGNALS. The dice arrive on the PACED replay view while the window arrives
   *    on the raw shell frame, and they are separate socket messages — so whichever lands first
   *    closes the gate and the other cannot slip through ahead of it.
   *  • OPEN AT MOUNT, and inert under reduced motion. A resume finds the dice already at rest and
   *    must show everything at once; with motion off they never fly, so there is nothing to wait
   *    for. Both are pinned by tests — an always-closed gate is its own frozen-board bug.
   */
  const nowMs = Date.now();
  /** Epoch at which the roll's consequences may be shown. Past ⇒ nothing is being withheld. */
  const revealAt = useRef(0);
  /** When the CURRENT hold began — a hard ceiling, so no stream of frames can hold the board shut. */
  const holdFrom = useRef(0);
  const holdReveal = (): void => {
    if (nowMs >= revealAt.current) holdFrom.current = nowMs; // not holding yet → a fresh hold
    revealAt.current = Math.min(nowMs + DIE_THROW_MS, holdFrom.current + REVEAL_CAP_MS);
  };
  /** The roll / the window this gate has already closed for — so closing it is IDEMPOTENT, which
   *  it must be: React renders every component twice under StrictMode. */
  const gatedRoll = useRef(-1);
  const gatedEndsAt = useRef<number | null>(null);
  if (!reduced && thrown.current && gatedRoll.current !== rollNo.current) {
    gatedRoll.current = rollNo.current;
    holdReveal();
  }

  /*
   * THE RING DRAWS THE WINDOW IT WAS ALREADY DRAWING UNTIL THE THROW IS OVER.
   *
   * A re-arm for the SAME seat is the roll's own window and nothing else: the server opens one
   * window when the turn starts and one when the dice land, and a reconnect re-sends the running
   * deadline VERBATIM. So a changed `endsAt` on an unchanged seat IS the roll — which makes this a
   * causal trigger rather than a guess about which frame arrives first. Everything else (the turn
   * changing hands, the shared §5.1 window, the ring clearing) is adopted immediately.
   */
  const shownTimer = useRef<BgTurnTimer | null>(timer ?? null);
  const liveTimer = timer ?? null;
  const rolledWindow =
    liveTimer !== null &&
    shownTimer.current !== null &&
    liveTimer.seat === shownTimer.current.seat &&
    liveTimer.endsAt !== shownTimer.current.endsAt;
  if (rolledWindow && !reduced && gatedEndsAt.current !== liveTimer.endsAt) {
    gatedEndsAt.current = liveTimer.endsAt;
    holdReveal();
  }
  /*
   * THE DOUBLE'S EXTRA TIME IS A GRANT, SO IT IS DRAWN AS ONE.
   *
   * One window covers the whole turn and a double stretches it to 1.5×, which means the remaining
   * time GOES UP once, mid-turn. That cannot be rendered away — no arithmetic makes an increase
   * look like a decrease — so the only question is whether it reads as a reward or a stutter.
   * Measured on the real controller, un-eased, it snaps the digits 7 → 17 and the arc 34% → 55% in
   * a single frame.
   *
   * Captured at the moment the window is ADOPTED (which the reveal gate has already deferred to the
   * dice landing), so the ease, the «+۱۰ ثانیه» chip and the ×N badge all belong to one beat.
   *
   * ⚠️ «THE SAME SEAT'S DEADLINE MOVED OUTWARD» IS THE WHOLE TRIGGER. A handover hands the OTHER
   * seat a full window, which is not extra time for anybody — matching on that would raise the chip
   * on every single turn.
   */
  const grant = useRef<{ from: BgTurnTimer; at: number; sec: number } | null>(null);
  if (!rolledWindow || nowMs >= revealAt.current) {
    const outgoing = shownTimer.current;
    if (
      liveTimer !== null &&
      outgoing !== null &&
      liveTimer.seat === outgoing.seat &&
      liveTimer.endsAt > outgoing.endsAt
    ) {
      const added = Math.round(
        ((liveTimer.totalMs ?? liveTimer.totalSec * 1000) -
          (outgoing.totalMs ?? outgoing.totalSec * 1000)) /
          1000,
      );
      if (added > 0) grant.current = { from: outgoing, at: nowMs, sec: added };
    }
    shownTimer.current = liveTimer;
  }
  /*
   * ⚠️ THE LOCAL MUST BE CLEARED IN THE SAME RENDER, not just the ref. Reading the ref into a const
   * and then nulling only the ref drew the chip ONE more time — and that render also computed a
   * `grantUntil` in the past, which tore the ticker down, so nothing ever re-rendered to remove it:
   * the chip stayed on screen for the rest of the game. Caught by the test that advances past the
   * chip's own lifetime; nothing else would have.
   */
  let granted = grant.current;
  if (granted !== null && nowMs - granted.at >= GRANT_CHIP_MS) {
    grant.current = null;
    granted = null;
  }
  /*
   * A FINE TICK FOR THE BEAT. The ring's own 250ms tick is far too coarse for a 600ms ease (it
   * would draw two frames of it), and a SOLO table has no ring to tick at all. Runs only while a
   * grant is on screen and stops itself, so an ordinary turn costs nothing.
   */
  const [, forceGrant] = useState(0);
  const grantUntil = granted !== null ? granted.at + GRANT_CHIP_MS : 0;
  useEffect(() => {
    if (grantUntil === 0 || Date.now() >= grantUntil) return undefined;
    const id = window.setInterval(() => forceGrant((n) => n + 1), 33);
    return () => window.clearInterval(id);
  }, [grantUntil]);
  /** False while the dice are still in the air: the ×N badge, the move hint and the roll's own
   *  window all wait for this. */
  const diceRead = nowMs >= revealAt.current;
  /*
   * A re-render AT the moment the throw ends. The ring's own 1s tick would eventually do it, but a
   * SOLO table has no ring at all — nothing else would re-render, so the badge and the hint would
   * sit hidden until the next server frame. Cleared on unmount, so nothing lands in the lobby.
   */
  const [, forceReveal] = useState(0);
  const revealDeadline = revealAt.current;
  useEffect(() => {
    const left = revealDeadline - Date.now();
    if (left <= 0) return undefined;
    const id = window.setTimeout(() => forceReveal((n) => n + 1), left);
    return () => window.clearTimeout(id);
  }, [revealDeadline]);

  /*
   * THE DICE STAY ON THE FELT — IN THE HALF OF WHOEVER THREW THEM.
   *
   * `applyMove("confirm")` clears `dice`, so the mover's roll used to vanish the instant they
   * confirmed — if you looked away you could never learn what they threw. A physical board leaves
   * the dice down until the NEXT player picks up their own to roll, so that is what happens here:
   * the last roll is held for display (the engine has already cleared it) and dropped the moment a
   * fresh roll lands. Dimmed while stale, so it is never mistaken for a live roll.
   *
   * ⚠️ AND THE SIDE MUST FOLLOW THE THROWER, NOT THE TURN. `rollerIsMe` used to read `view.turn`,
   * which changes at the confirm — so the moment I finished my turn my own dice slid across the
   * spine into the OPPONENT's half and sat there, faint, until they rolled. Measured at 390×844:
   * the tray's centre went 294 → 96 within 200ms of the confirm. That stale pair, at 0.5 × 0.4
   * opacity, is also the «faint ghost die before the real throw» — the corpse of the last roll
   * parked exactly where the next one will land. The thrower is remembered alongside the dice.
   */
  const heldDice = useRef<readonly number[]>(view.dice);
  const heldRoller = useRef<0 | 1>(view.turn === 0 ? 0 : 1);
  if (view.dice.length > 0) {
    heldDice.current = view.dice;
    heldRoller.current = view.turn === 0 ? 0 : 1;
  }
  const diceAreStale = view.dice.length === 0 && heldDice.current.length > 0;
  const feltDice = view.dice.length > 0 ? view.dice : heldDice.current;
  /** Whose throw is on the felt right now — the live roller, or whoever left the last pair there. */
  const diceRoller: 0 | 1 = view.dice.length > 0 ? (view.turn === 0 ? 0 : 1) : heldRoller.current;
  const rollerIsMe = diceRoller === you;

  /*
   * §5.2 — the frozen hitter, STRAIGHT FROM THE ENGINE (`view.frozen`). It used to be accumulated
   * here from the replay's own geometric guess, which raised the notice in three situations where
   * nothing was frozen at all — on a double, once the no-other-move exception had lifted the ban,
   * and with no dice left — and on a VIEWER's screen whenever the opponent hit inside their home.
   * The engine populates this only for the seat on turn, so all four are impossible by construction.
   * No local state and no clearing effect: a new turn has no staged moves, so it arrives empty.
   */
  const frozen: readonly number[] = frozenProp ?? view.frozen ?? [];
  /** §5.2 EXCEPTION 2 — the hitter is free because there is no other move. Rare, and worth saying. */
  const frozenLifted: readonly number[] = view.frozenLifted ?? [];

  /** The point flashing from a hit right now (§4 — the impact the sound used to have to carry alone). */
  const hitFlashAt =
    replay.event?.kind === "walk" && replay.event.hit !== null ? replay.event.hit : null;

  /*
   * ⚠️ THE «LAST TURN» TRAIL IS GONE (operator, on device, 2026-08-15), AND IT COULD NEVER HAVE
   * WORKED. It marked both the from AND the to spot of every sub-move in `view.lastMove` with a
   * faint gold ring (`.bg-trail`) while `view.dice.length === 0` — from a confirm until the next
   * roll, which against a paced bot is about a second.
   *
   * The «to» half was invisible BY CONSTRUCTION: the marker sits at the point's BASE, which is
   * exactly where the chip stack starts, and that stack renders in a `zIndex: 1` wrapper — while
   * every numbered destination has, by definition, a checker on it the moment the move lands (a
   * bear-off's `to` is `"off"`, which is a string and never matches a numbered point). So the only
   * markers a player could EVER see were orphan rings on the points a checker had just left: no
   * from→to relationship, no label, gone in a second, and duplicating the paced replay they had
   * just watched. Reported as «a faint yellow circle I don't know the meaning of», which is exactly
   * what it was. The legal-source affordance (`.bg-point--from`) is a different thing and stays.
   */

  /* MEASURED GEOMETRY — declared before the flight helpers, which size a checker's travel from it.
     Checkers are sized from the MEASURED point width, published as `--pt-w`: without it the chips
     were a hardcoded 24px inside a 22.7px point and spilled onto their neighbours. */
  const boardRef = useRef<HTMLDivElement | null>(null);
  // The same measurement the chips use, kept in state so the FLYING checker matches their size.
  const [checkerPx, setCheckerPx] = useState(22);
  useLayoutEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      // `.bg-point` and NOT `[data-testid^="spot-"]` — the latter also matches the tray
      // (`spot-off`) and the bar (`spot-bar-0`), and the tray is the first such node in the DOM,
      // so it measured the 26px rack instead of a 20.6px point.
      const pt = el.querySelector(".bg-point") as HTMLElement | null;
      const w = pt?.getBoundingClientRect().width ?? 0;
      // 2px of breathing room so adjacent stacks never touch.
      if (w > 0) {
        const chip = Math.max(10, Math.round(w) - 2);
        el.style.setProperty("--pt-w", `${chip}px`);
        setCheckerPx((prev) => (prev === chip ? prev : chip));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Checker FLIGHT: when a sub-move lands (staged grows — for the player OR the paced bot) a chip
  // flies from the move's SOURCE to its DESTINATION over the board, then vanishes. Every spot —
  // each point, YOUR bar, and the bear-off rack — registers its element here, so bar entries and
  // bear-offs animate too (previously only points had refs, so those two moves simply teleported).
  const spotRefs = useRef(new Map<string, HTMLElement>());
  /** `spot` is a point index, or a per-owner composite (`bar-0`, `off-1`) — the bar and the tray each
   *  have TWO ends, and registering only the local player's meant an opponent's bar entry flew out of
   *  YOUR bar and every bear-off landed at the rack's midpoint. */
  const setSpotRef = (spot: BgSpot | string) => (el: HTMLElement | null) => {
    const key = String(spot);
    if (el) spotRefs.current.set(key, el);
    else spotRefs.current.delete(key);
  };
  interface Flight {
    key: number;
    from: { x: number; y: number };
    to: { x: number; y: number };
    mine: boolean;
    /** The blot being knocked to the bar by this same walk, if any. */
    knock: { from: { x: number; y: number }; to: { x: number; y: number } } | null;
    /** The spot this walk is travelling TO — the destination holds its checker back until it lands. */
    toSpot: BgSpot;
    /** Whose checker is travelling (so `off`/`bar` subtract from the right side). */
    toOwner: 0 | 1;
    /** The side whose bar gains a knocked blot from this walk, if any. */
    knockOwner: 0 | 1 | null;
    /** The POINT a blot is travelling back to (an undo of a hit), if any — held back there while
     *  it flies, exactly as `knockOwner` holds one back on the bar. */
    unhitSpot: number | null;
  }
  const [flights, setFlights] = useState<Flight[]>([]);
  const flightNo = useRef(0);
  /** key → its removal timeout. Held in a ref so no re-render can cancel a landing. */
  const flightTimers = useRef(new Map<number, number>());

  /*
   * A CHECKER IN THE AIR IS NOT YET ON THE BOARD.
   *
   * The paced view already has the sub-move applied, so a destination point/bar/tray would render
   * the arriving checker at full strength while an identical copy was still flying towards it —
   * measured: the destination went 0 → 1 at t=11ms, i.e. the instant of the tap, with the flight
   * then sliding in over the finished result for the next 700ms. These helpers subtract whatever
   * is still travelling, so the board shows the position the player is actually being shown.
   */
  const arrivingAt = (spot: number): number =>
    flights.reduce(
      // Two ways a checker lands on a point: the walk itself, or an opponent's blot coming BACK
      // from the bar because the walk that hit it was taken back.
      (n, f) => n + (f.toSpot === spot ? 1 : 0) + (f.unhitSpot === spot ? 1 : 0),
      0,
    );
  const arrivingOff = (owner: 0 | 1): number =>
    flights.reduce((n, f) => (f.toSpot === "off" && f.toOwner === owner ? n + 1 : n), 0);
  const arrivingOnBar = (owner: 0 | 1): number =>
    flights.reduce((n, f) => {
      // Two ways a checker lands on a bar: it was KNOCKED there by this walk, or the walk itself
      // is travelling to the bar (an undo of a bar entry runs the arc backwards).
      const knocked = f.knockOwner === owner ? 1 : 0;
      const walked = f.toSpot === "bar" && f.toOwner === owner ? 1 : 0;
      return n + knocked + walked;
    }, 0);

  const centre = (el: HTMLElement): { x: number; y: number } => {
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };
  const spotAt = (spot: BgSpot, owner: 0 | 1): HTMLElement | undefined =>
    spotRefs.current.get(spot === "bar" || spot === "off" ? `${spot}-${owner}` : String(spot));

  const rows = orientRows(you);
  /*
   * WHERE A CHECKER ACTUALLY SITS IN A POINT — the same seating {@link Stack} uses.
   *
   * A flight used to run between the two points' geometric CENTRES, and chips seat at a point's
   * BASE and pile toward its apex. Measured at 390×844: the checker landed at y 614, the point's
   * centre, while the checker it became settled at y 687 — 73px lower. So it rose above the
   * destination, hovered there for the rest of its walk, and then "dropped" as the static chip
   * appeared. The departure was ~70px out for the same reason. It now travels between the real
   * slots, which is why {@link CHIP_GAP} is shared rather than repeated.
   */
  /** One slot of a stack that grows from `top ? the box's top : its bottom`. */
  const stackSlot = (
    el: HTMLElement,
    index: number,
    top: boolean,
    pad: number,
  ): { x: number; y: number } => {
    const r = el.getBoundingClientRect();
    const step = checkerPx + CHIP_GAP;
    // A stack over five collapses to four chips + a stamped count, so the seating saturates there.
    const i = Math.min(Math.max(index, 0), 4);
    return {
      x: r.left + r.width / 2,
      y: top ? r.top + pad + i * step + checkerPx / 2 : r.bottom - pad - i * step - checkerPx / 2,
    };
  };
  const slotCentre = (el: HTMLElement, spot: number, index: number): { x: number; y: number } =>
    stackSlot(el, index, rows.top.includes(spot), POINT_PAD);
  /**
   * The slot a walking checker leaves from / lands on. The paced view has ALREADY applied the
   * sub-move when this runs, so the source count is the slot just vacated and the destination count
   * includes the arriving checker — which is therefore always the TOP of the stack ({@link flightSlot}).
   *
   * ⚠️ THE BAR IS A STACK, NOT A POINT. It used to fall through to `centre(el)` — the box's
   * geometric middle — so EVERY knocked blot flew to the same pixel however many were already
   * there, while the static chips seat apart. Two hits in one turn (your own sub-moves are not
   * paced, so both fly at once) landed on top of each other: the reported «their movement
   * animations appear to overlap».
   */
  const endpoint = (
    spot: BgSpot,
    owner: 0 | 1,
    end: "from" | "to",
  ): { x: number; y: number } | null => {
    const el = spotAt(spot, owner);
    if (!el) return null;
    if (spot === "off") return centre(el); // the tray is a ladder of its own, not a stack
    if (spot === "bar") {
      // The bar grows from its OWNER's own board edge, exactly like the points do (see `barBtn`).
      return stackSlot(el, flightSlot(view.bar[owner], end), owner !== you, 0);
    }
    return slotCentre(el, spot, flightSlot(Math.abs(view.points[spot] ?? 0), end));
  };

  /*
   * ONE flight per replay event, each owning its own removal timer.
   *
   * The previous version held a SINGLE flight in state: a second sub-move remounted it (so the first
   * chip vanished mid-air) and the first one's stale `setTimeout` then nulled the second early. With a
   * keyed list, concurrent walks simply coexist — which is also what makes fast tapping honest
   * instead of lossy.
   */
  useEffect(() => {
    const e = replay.event;
    if (!e || prefersReducedMotion()) return undefined;
    if (e.kind !== "walk" && e.kind !== "undo") return undefined;
    const mover: 0 | 1 = e.mover === 0 ? 0 : 1;
    const opp: 0 | 1 = mover === 0 ? 1 : 0;
    // An undo runs the same arc BACKWARDS.
    const src = e.kind === "walk" ? e.sub.from : e.sub.to;
    const dst = e.kind === "walk" ? e.sub.to : e.sub.from;
    const a = endpoint(src, mover, "from");
    const b = endpoint(dst, mover, "to");
    if (!a || !b) return undefined;
    const hitPoint = e.kind === "walk" ? e.hit : null;
    /*
     * THE OPPONENT'S BLOT MOVES BOTH WAYS.
     *
     * A walk that HITS throws it to the bar. An UNDO of that walk brings it back — and the undo
     * event has carried `unhit` all along while this effect ignored it, so the blot simply
     * reappeared on the point at t=0 while our own checker took 700ms to walk home. One thing
     * teleporting beside another thing animating is the «undo isn't clean» report.
     */
    const unhitPoint = e.kind === "undo" ? e.unhit : null;
    // The struck blot leaves (or returns to) the slot it sits in — a lone enemy checker is always
    // the base of its point, i.e. index 0.
    const blotPoint = hitPoint ?? unhitPoint;
    const blotEl = blotPoint !== null ? spotRefs.current.get(String(blotPoint)) : undefined;
    const barSlot = endpoint("bar", opp, hitPoint !== null ? "to" : "from");
    flightNo.current += 1;
    const key = flightNo.current;
    const knock =
      blotEl && barSlot
        ? hitPoint !== null
          ? { from: slotCentre(blotEl, blotPoint as number, 0), to: barSlot } // hit → to the bar
          : { from: barSlot, to: slotCentre(blotEl, blotPoint as number, 0) } // undo → back home
        : null;
    setFlights((f) => [
      ...f,
      {
        key,
        from: a,
        to: b,
        mine: mover === you,
        knock,
        // WHERE this walk is going, so the destination can HOLD BACK the arriving checker until
        // it actually lands (see `arriving`). Without it the board showed the move as finished the
        // instant it was tapped and then flew a duplicate over the top of it.
        toSpot: dst,
        toOwner: mover,
        // A knocked blot lands on the OPPONENT's bar — held back there for the same reason.
        // ⚠️ HIT ONLY. On an UNDO the blot LEAVES the bar (the paced view has already taken it
        // off), so counting it as arriving would show one checker too few up there.
        knockOwner: knock && hitPoint !== null ? opp : null,
        unhitSpot: knock && unhitPoint !== null ? unhitPoint : null,
      },
    ]);
    /*
     * ⚠️ THE REMOVAL TIMER MUST NOT BE THIS EFFECT'S CLEANUP.
     *
     * It used to be `return () => clearTimeout(id)` — and `replay.event` goes back to NULL one
     * render after the walk, so React ran that cleanup and CANCELLED the removal. The flight was
     * then never taken out of state: measured, a single move left its chip in the list for the
     * whole 4s window and beyond. It used to be merely invisible (a duplicate parked exactly on
     * top of the real checker); the moment the destination started holding its checker back, that
     * leak became a checker that never lands. Timers are owned by a ref and cleared on UNMOUNT.
     */
    flightTimers.current.set(
      key,
      window.setTimeout(
        () => {
          setFlights((f) => f.filter((x) => x.key !== key));
          flightTimers.current.delete(key);
          // ⚠️ HIT_MS is the knock's OWN budget and must only be spent when something was knocked.
          // Adding it unconditionally left the flown checker parked, motionless, on the destination
          // for ~700ms after its 700ms walk had already finished — the "ghost" half of the defect.
        },
        WALK_MS + (knock ? HIT_MS : 0) + 80,
      ),
    );
    return undefined;
  }, [replay.event, you]);

  // Flight timers are owned here, not by the effect above, so a re-render can never cancel one.
  useEffect(() => {
    const timers = flightTimers.current;
    return () => {
      for (const id of timers.values()) window.clearTimeout(id);
      timers.clear();
    };
  }, []);

  /*
   * AUTO-CONFIRM (operator's call, 2026-08-09).
   *
   * `mustConfirm` means the engine has nothing left to offer: either a MAXIMAL turn is staged, or
   * the roll produced no legal move at all. Making the player press «تأیید» in that state adds no
   * decision — and on a blocked turn it stalls the table until a human notices. So it commits
   * itself after a visible ~2s countdown, and «برگرداندن» cancels it by simply un-staging.
   *
   * NEVER while the replay is draining or a checker is in the air: committing under a flight would
   * confirm a position the player has not yet been shown.
   */
  /*
   * BETWEEN دست‌ها. A won round holds the final position (`roundResult`) so both players can read
   * how it ended and what it scored, then the next one is dealt. `nextRound` is legal for EITHER
   * seat, so whichever client gets there first unsticks it and nobody waits on the other.
   *
   * ROUND_HOLD_MS is LOCKSTEP with the engine's own `postMoveDelayMs` for this state — the beat a
   * bot waits before dealing. Change one and a bot's round break stops matching a person's.
   */
  const roundResult = view.roundResult;
  const canNextRound = legal.some((m) => m.kind === "nextRound");
  useEffect(() => {
    if (!roundResult || !canNextRound || replay.draining) return undefined;
    const id = window.setTimeout(() => onMoveRef.current({ kind: "nextRound" }), ROUND_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [roundResult, canNextRound, replay.draining]);

  const canConfirm = legal.some((m) => m.kind === "confirm");
  /*
   * ⚠️ CORRECTED 2026-08-09, same day, on the operator's word: «what is تأیید خودکار? i need a
   * confirmation to proceed SOONER».
   *
   * The first cut made EVERY completed turn wait out a countdown, which is dead time when the
   * player already knows they are done. A «تأیید» button is now offered the instant `mustConfirm`
   * is true, so a turn can always be committed immediately.
   *
   * The countdown survives ONLY for a BLOCKED turn (`noLegalMove`) — there the player has nothing
   * to decide and nothing to look at, and without it the table simply stalls until a human notices.
   * So: a decision is never rushed, and a non-decision never stalls.
   *
   * ⚠️ card-98 item 6 (operator, 2026-10-27): «the turn passes without a confirm» — a blocked turn
   * shows «حرکتی نداری» alone: no «تأیید», no «تأیید خودکار» countdown. Two shapes reach it:
   *   · NO ROLL CAN PLAY (closed out / a full prime): the SERVER passes it on its own clock and
   *     offers nothing (`legal: []`), so nothing below arms;
   *   · a DANCED roll: still 4ef63408's flow (the operator's 6.2, 2026-10-28: «keep the old pace»),
   *     so `confirm` is offered and THIS sends it, silently, on the same 2000 ms beat as before —
   *     after the replay has shown the roll (2600 ms), or at once under reduced motion. A table
   *     timer could not keep that pace: it cannot see this board's replay or its motion setting.
   */
  const autoArmed =
    isMyTurn && view.noLegalMove && canConfirm && !replay.draining && flights.length === 0;
  // `onMove` is often an inline arrow, so it must NOT be an effect dependency — a changing dep
  // would restart the timer on every render and it would never fire.
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  useEffect(() => {
    if (!autoArmed) return undefined;
    const fire = window.setTimeout(() => onMoveRef.current({ kind: "confirm" }), NO_MOVE_NOTICE_MS);
    return () => window.clearTimeout(fire);
  }, [autoArmed]);

  // The board is sized to the MEASURED available box (explicit px), NOT its own flex-shrink —
  // otherwise on the Telegram/Android WebView the board refuses to shrink and the روll/confirm
  // control bar drops below the fold ("can't play"). See useBoxHeight.
  const box = useBoxHeight();
  /*
   * The board's own budget: the measured box MINUS the two fixed-height panels and the gaps around
   * them. Passing the FULL box height is the bug that clipped منچ — the chrome is laid out after the
   * board and the column then overflows into a `justify-*` + `overflow-hidden` parent.
   *
   * `useBoxHeight` now reports the CONTENT box, so the group's padding — `BOARD_PAD_BOTTOM` AND the
   * home-indicator inset — is already out of this number. Subtracting `BOARD_PAD_BOTTOM` a second
   * time here is what double-counted it; see that hook for the measured overlap it caused.
   */
  const fit = boardGeometry(
    Math.max(160, box.width || 352),
    Math.max(150, (box.height || 480) - PANEL_H * 2 - 18),
  );
  /** The dice scale with the BAND they rest in, so they are as large as they can be without ever
   *  reaching a point. Clamped so a small phone stays readable and a wide one does not get comical. */
  const dieSize = Math.max(26, Math.min(40, Math.round(fit.bandH * 0.46)));

  /*
   * §5.1 — THE CONTEST ROW, SOLVED: [ die · gap · message · gap · die ], symmetric.
   *
   * The two dice used to be anchored `right:4 / left:4` inside a `width:40%` slot, which put each
   * die's CENTRE at 21% of the felt from its own edge — out near the rails — while the message,
   * capped at 44%, came within 8–10px of them (measured at 320/375/390/430/440/480). So the dice
   * read as pushed into the corners AND crowded the toast at the same time.
   *
   * Both numbers are now derived from the board rather than written down: the die sits at
   * {@link CONTEST_DIE_CENTRE} of the felt from its edge — further IN, but never closer to the rail
   * than a single point's width — and the message is capped so a real gap is left on both sides.
   */
  const contestSlotW = dieSize + 6;
  const contestInset = Math.max(
    Math.round(fit.pointW),
    Math.round(fit.feltW * CONTEST_DIE_CENTRE - contestSlotW / 2),
  );

  const legalTargets: readonly BgSpot[] =
    selected !== null ? (view.legalTo[String(selected)] ?? []) : [];

  const tapSpot = (spot: BgSpot): void => {
    // While the replay drains the board is deliberately BEHIND the authoritative position, so a tap
    // would act on a position the player is not being shown. A tap instead fast-forwards to it.
    if (replay.draining) {
      replay.catchUp();
      return;
    }
    if (!isMyTurn) {
      // Tapping during the opponent's turn now SAYS so (the reference's «It is not your turn.»)
      // instead of silently doing nothing, which reads as a dead board.
      setNudge(fa.backgammon.notYourTurn);
      window.setTimeout(() => setNudge(null), 1600);
      return;
    }
    if (view.dice.length === 0) return;
    // Second tap on a legal destination for the current source → commit the sub-move.
    if (selected !== null && legalTargets.includes(spot)) {
      onMove({ kind: "move", from: selected, to: spot });
      setSelected(null);
      return;
    }
    // A second tap on the SAME source CANCELS it (the destinations go dark and nothing is sent).
    // Re-selecting a source you had already chosen was a no-op, so a player who changed their mind
    // had no way back except tapping something illegal — which shakes the board and sounds a thunk
    // at someone who did nothing wrong.
    if (selected !== null && selected === spot) {
      setSelected(null);
      return;
    }
    // First tap on a legal source → select it (its destinations light up).
    if (view.legalFrom.includes(spot)) {
      setSelected(spot);
      return;
    }
    // Neither a legal destination nor a (re)selectable source. With a source already chosen this is
    // an ILLEGAL destination → a dull thunk + a brief shake of that source, then clear (mirror chess).
    if (selected !== null) {
      playSfx("bgIllegal");
      if (!reduced) {
        setShakeSpot(selected);
        window.setTimeout(() => setShakeSpot(null), 260);
      }
    }
    setSelected(null);
  };

  const ownerOf = (i: number): "you" | "opp" | null => {
    const v = view.points[i] ?? 0;
    if (v === 0) return null;
    const seat0 = v > 0; // +n = seat 0's checkers, -n = seat 1's
    return (seat0 ? 0 : 1) === you ? "you" : "opp";
  };

  const renderPoint = (spot: number, row: "top" | "bottom", col: number): ReactNode => {
    const count = Math.max(0, Math.abs(view.points[spot] ?? 0) - arrivingAt(spot));
    const who = ownerOf(spot);
    const isFrom = view.legalFrom.includes(spot);
    const isTo = legalTargets.includes(spot);
    const isFrozen = frozen.includes(spot);
    const isFlashing = hitFlashAt === spot;
    // Per the design: the top row starts ivory, the bottom row starts teal, so the colours
    // interlock across the central bar (02-board-premium.html points 13-24 vs 1-12).
    const ivory = row === "top" ? col % 2 === 0 : col % 2 === 1;
    return (
      <button
        key={spot}
        ref={setSpotRef(spot)}
        type="button"
        data-testid={`spot-${spot}`}
        onClick={() => tapSpot(spot)}
        className={`bg-point${isFrom ? " bg-point--from" : ""}${isTo ? " bg-point--to" : ""}${
          selected === spot ? " bg-point--sel" : ""
        }${shakeSpot === spot ? " bg-illegal" : ""}${isFrozen ? " bg-frozen" : ""}${
          isFlashing ? " bg-hit-flash" : ""
        }`}
        style={{
          appearance: "none",
          background: "transparent",
          border: 0,
          // The six points TILE their half exactly (flex:1 each) and the row owns the height, so a
          // point is always `pointW × pointH` — the proportion `boardGeometry` solved for. The WHOLE
          // rect is the hit-area; the clip-path triangle sits absolute-inset behind the chips.
          padding: "2px 0",
          flex: "1 1 0",
          minWidth: 0,
          height: "100%",
          display: "flex",
          // Chips seat at the point's BASE (the board edge) and pile toward the apex: the top row
          // fills from the top edge down, the bottom row from the bottom edge up.
          flexDirection: row === "top" ? "column" : "column-reverse",
          alignItems: "center",
          cursor: isMyTurn && (isFrom || isTo) ? "pointer" : "default",
        }}
        aria-label={`${fa.backgammon.point} ${faNum(spot + 1)}`}
      >
        <span
          aria-hidden
          className={`bg-point__tri bg-point__tri--${row === "top" ? "down" : "up"} bg-point__tri--${
            ivory ? "ivory" : "dark"
          }`}
        />
        <span style={{ position: "relative", zIndex: 1 }}>
          <Stack count={count} mine={who === "you"} row={row} />
        </span>
      </button>
    );
  };

  const { top, bottom } = orientRows(you);

  /** The central bar for one colour. `color === you` is YOUR bar (the interactive source). */
  const barBtn = (color: 0 | 1): ReactNode => (
    <button
      key={`bar-${color}`}
      type="button"
      // BOTH bars register (keyed by owner): only yours is a move SOURCE, but the opponent's is a
      // flight ENDPOINT every time you knock one of their checkers onto it, and the origin of every
      // entry they make. Registering only yours sent their entries flying out of your own bar.
      ref={setSpotRef(`bar-${color}`)}
      data-testid={`spot-bar-${color}`}
      onClick={() => tapSpot("bar")}
      disabled={color !== you}
      /* HIGHLIGHTED ONLY WHILE THE BAR IS ACTUALLY A LEGAL SOURCE.
         `mustEnterFromBar` merely says a checker is still up there — so with two on the bar and
         only one enterable, the bar went on saying «enter me» while the single legal move left was
         «تأیید». A highlight has to mean actionable. `legalFrom` is exactly that, and it also
         covers the blocked case (every entry point shut ⇒ nothing to do ⇒ nothing lit). */
      className={`bg-bar${
        view.legalFrom.includes("bar") && color === you ? " bg-bar--active" : ""
      }${shakeSpot === "bar" && color === you ? " bg-illegal" : ""}`}
      style={{
        appearance: "none",
        border: 0,
        background: "transparent",
        cursor: color === you && isMyTurn ? "pointer" : "default",
        display: "flex",
        justifyContent: "center",
        // An EMPTY bar renders no chips, so without this the button collapsed to zero height — and
        // a knocked blot then flew to a degenerate box at the very edge of the spine instead of to
        // the bar. Reserving one checker's worth keeps the landing honest.
        minHeight: checkerPx,
        alignItems: color === you ? "flex-end" : "flex-start",
      }}
      aria-label={fa.backgammon.bar}
    >
      {/* Opponent's bar sits in the top half, yours in the bottom half → each grows from its own
          board edge, matching the points. */}
      <Stack
        count={Math.max(0, view.bar[color] - arrivingOnBar(color))}
        mine={color === you}
        row={color === you ? "bottom" : "top"}
      />
    </button>
  );

  /** The bear-off tray — a rack OUTSIDE the playing field (a physical board never stacks borne-off
   *  checkers on a point; both reference boards keep it as its own column). A ladder of 15 slots:
   *  the opponent's half on top, yours below, each lighting as a checker comes off. The count
   *  renders only once something is actually off — the old always-on «۰» sat on the felt and read
   *  as a stray shiny dot, and the two decorative bars above it as two shadows. */
  /* ── BEAR-OFF POCKETS ─────────────────────────────────────────────────────
     Sunk into the RAILS (the reference keeps its trays there, which is what frees the felt to use
     the whole width), on the player's own HOME side so a checker bears off TOWARD its own tray.
     Home measures visual-LEFT for the local player under the app's RTL flow — see the note on
     `orientRows`, whose docstring used to claim the opposite. Physical `left`, never
     `inset-inline-*`: the documented RTL trap that once put a toast off screen. */
  const trayIsTarget = legalTargets.includes("off");
  const pocket = (owner: 0 | 1): ReactNode => {
    const mine = owner === you;
    const n = Math.max(0, view.off[owner] - arrivingOff(owner));
    const live = mine && trayIsTarget;
    return (
      <button
        type="button"
        ref={setSpotRef(`off-${owner}`)}
        data-testid={mine ? "spot-off" : `spot-off-${owner}`}
        onClick={live ? () => tapSpot("off") : undefined}
        disabled={!live}
        aria-label={`${fa.backgammon.bearOff} ${faNum(n)}`}
        className={`${BTN} bg-pocket${live ? " bg-pocket--to" : ""}`}
        style={{
          position: "absolute",
          left: RIM_X + 2,
          top: 4,
          bottom: 4,
          width: "34%",
          border: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          padding: "0 5px",
          pointerEvents: live ? "auto" : "none",
        }}
      >
        {/* Fifteen slats, filling as checkers come off — a tray seen from above. */}
        {Array.from({ length: 15 }, (_, i) => (
          <span
            key={i}
            style={{
              flex: 1,
              minWidth: 0,
              height: "58%",
              borderRadius: 1,
              background:
                i < n
                  ? mine
                    ? "linear-gradient(to bottom, #ffe088, #a5820e)"
                    : "linear-gradient(to bottom, #4a5158, #0d1114)"
                  : "rgb(255 255 255 / 0.05)",
            }}
          />
        ))}
        {n > 0 ? (
          <span
            className="font-numeral tabular-nums"
            style={{
              marginLeft: 4,
              fontSize: 11,
              fontWeight: 800,
              color: "rgb(var(--c-secondary))",
            }}
          >
            {faNum(n)}
          </span>
        ) : null}
      </button>
    );
  };

  /** One rail of the wooden rim, carrying that side's bear-off pocket. */
  const rail = (owner: 0 | 1): ReactNode => (
    <div style={{ position: "relative", height: RAIL_H, flex: "none" }}>{pocket(owner)}</div>
  );

  /** A row of six points — the row owns the LENGTH, each point takes a sixth of the width. */
  const pointRow = (spots: readonly number[], row: "top" | "bottom", base: number): ReactNode => (
    <div
      style={{
        height: fit.pointH,
        flex: "none",
        display: "flex",
        alignItems: row === "top" ? "flex-start" : "flex-end",
      }}
    >
      {spots.map((s, i) => renderPoint(s, row, i + base))}
    </div>
  );

  /** One half of the felt: points, the empty band, points. */
  const half = (from: number): ReactNode => (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {pointRow(top.slice(from, from + 6), "top", from)}
      {pointRow(bottom.slice(from, from + 6), "bottom", from)}
    </div>
  );

  /**
   * Which die is BEING CONSUMED right now — i.e. a sub-move is in flight on it.
   *
   * That is the only honest reading of the reference's amber die: `legalFrom`/`legalTo` never say
   * which die a move would spend, so we do not invent a per-die selection UI the view cannot back.
   * The replay event carries only from/to, so the value is derived from the distance travelled
   * (seat 0 runs 23→0, seat 1 runs 0→23; a bar entry lands on the die's own point; a bear-off may
   * legally OVERSHOOT, so it takes the smallest die that could have done it).
   */
  const dieOfSub = (sub: { from: BgSpot; to: BgSpot }, mover: 0 | 1): number | null => {
    if (typeof sub.from === "number" && typeof sub.to === "number")
      return Math.abs(sub.to - sub.from);
    if (sub.from === "bar" && typeof sub.to === "number")
      return mover === 0 ? 24 - sub.to : sub.to + 1;
    if (sub.to === "off" && typeof sub.from === "number") {
      const exact = mover === 0 ? sub.from + 1 : 24 - sub.from;
      const over = view.dice.filter((d) => d >= exact).sort((a, b) => a - b);
      return over[0] ?? exact;
    }
    return null;
  };
  const activeDieValue =
    replay.event?.kind === "walk"
      ? dieOfSub(replay.event.sub, replay.event.mover === 0 ? 0 : 1)
      : null;

  /**
   * Which of the two drawn dice is spent.
   *
   * A NON-double shows two different numbers, so a die is spent once its value has left
   * `diceRemaining`. A DOUBLE is ONE number played four times — `includes` therefore stayed true
   * until all four were gone, so both dice sat BRIGHT through half the turn and nothing on the felt
   * said how much of it was left.
   *
   * Two dice, dimmed per PAIR (operator's call, 2026-08-14: «keep TWO dice, like a real board … the
   * physical two-dice look matters more than the exact count»). Each die stands for two of the four
   * moves and dims when that pair is used up; the ×N badge beside them still carries the exact
   * number, so the odd counts are not lost.
   */
  const dieSpent = (k: number): boolean => {
    if (feltDice.length === 4) return feltDice.length - view.diceRemaining.length >= 2 * (k + 1);
    const d = feltDice[k];
    return d === undefined || !view.diceRemaining.includes(d);
  };

  /** The one-shot §5.1 ceremony, while it is playing. */
  const opening = replay.event?.kind === "openingRollOff" ? replay.event : null;

  /*
   * §5.1 — THE CONTEST, WHILE IT IS BEING THROWN (M1).
   *
   * «The game doesn't wait for me to throw the opening die» was literally true: the engine rolled
   * both dice inside `createInitialState`. It is a real phase now — each player throws their own
   * single die — so the band shows each side's slot, empty until that side has thrown, and the
   * action slot offers «تاس بریز» while YOUR die is still to come.
   */
  const contest = view.openingRoll;
  const canOpeningRoll = legal.some((m) => m.kind === "openingRoll");
  /**
   * That seat's contest die — the live throw, or the pair the contest just TIED on.
   *
   * The engine resolves the second throw in the SAME call that rolls it, and on a tie it wrote
   * `[null, null]` — so the second player's own die was never in any frame and they watched it
   * vanish under «مساوی». It is held on `openingRoll.tied` until somebody throws again.
   */
  const contestTied = contest?.tied ?? null;
  const contestDie = (seat: 0 | 1): number | null =>
    contest?.dice[seat] ?? contest?.tied?.[seat] ?? null;
  /**
   * Which ROUND of the contest these slots are showing. A tie starts a new one, and the held pair
   * is a distinct state within it — so the dice REMOUNT at each boundary, which is what lets the
   * re-throw animate. (`Die3D` latches `throwing` at mount and never re-reads it.)
   *
   * ⚠️ IT IS HELD, BECAUSE RESOLVING A CONTEST IS NOT STARTING A NEW ONE. This read
   * `${contest?.ties ?? 0}-…` — and `contest` is UNDEFINED the instant the contest resolves, so
   * `?? 0` collapsed a post-tie round ("1-n") straight back to "0-n". That reads as a round
   * BOUNDARY: the per-seat «already thrown» latch reset and both slots remounted, so the
   * opponent's long-settled die threw again beside the one that had just landed. Reported from
   * prod («the bot's throw animation replays at the same moment as mine») and reproduced in a
   * real browser — see `backgammon-contest-tie.test.tsx` for the frame-by-frame trace. A tie-free
   * contest reads "0-n" throughout, which is why it only ever showed after a «مساوی».
   *
   * So the round identity only MOVES while a contest is live, and it carries the دست
   * (`view.round`) — a fresh round deals a brand-new contest whose dice must animate.
   *
   * ⚠️⚠️ AND THE HELD `tied` PAIR IS NOT A NEW ROUND — IT IS THE ROUND THAT JUST ENDED.
   *
   * This also carried a `-t`/`-n` marker for «is a tied pair being displayed», which made the tie
   * itself a round BOUNDARY (`"0-n"` → `"1-t"`): the latch reset and both slots remounted, so the
   * opponent's die — sitting on the felt for seconds — threw again in the very instant the
   * deciding one landed. That is «the bot's throw replays at the same moment as mine», reported
   * from prod and reproduced on the LIVE client. It was written down once as «accepted, it reads
   * as both thrown together»; on a device it reads as the bot throwing twice.
   *
   * The engine clears `dice` and holds the pair on `tied` in the SAME call that increments `ties`
   * (§6 — the second die exists in no other frame), so the pair on screen belongs to round
   * `ties − 1`. Say exactly that, and every case falls out: the tie frame keeps the round, so only
   * the die that just appeared animates; the re-throws that follow are round `ties` and DO remount
   * and animate; the resolution keeps the held round, so only the deciding die animates.
   */
  const liveContestRound = contest ? String(contest.ties - (contestTied ? 1 : 0)) : null;
  const heldContestRound = useRef(liveContestRound ?? "0");
  // Idempotent (same input ⇒ same value), so it is safe during render — StrictMode renders twice.
  if (liveContestRound !== null) heldContestRound.current = liveContestRound;
  const contestKey = `${view.round}:${heldContestRound.current}`;

  /*
   * WHICH CONTEST DIE IS LANDING RIGHT NOW.
   *
   * A die throws exactly once — when its value appears. «Is the contest still live?» is the WRONG
   * test for that, because the SECOND die to land is the one that RESOLVES the contest: by the time
   * that frame reaches us `openingRoll` is already gone, so the opponent's throw would play no
   * animation at all. Tracked per seat instead.
   *
   * Adopt-at-mount (the `thrown` discipline above): a die already on the table when we arrive was
   * thrown before we got here, so a resume never replays it. Both slots reset whenever the contest
   * is empty again — which is a fresh دست AND a tie, both of which really are new throws.
   */
  const contestValues: readonly [number | null, number | null] = [
    contestDie(0) ?? view.opening?.[0] ?? null,
    contestDie(1) ?? view.opening?.[1] ?? null,
  ];
  const contestThrown = useRef<[boolean, boolean]>([false, false]);
  const contestAdopted = useRef(false);
  if (!contestAdopted.current) {
    contestAdopted.current = true;
    contestThrown.current = [contestValues[0] !== null, contestValues[1] !== null];
  }
  // Resetting to «neither has been thrown» is IDEMPOTENT, so it is safe to do while rendering —
  // running it twice (React StrictMode renders every component twice in dev) yields the same
  // answer. MARKING one as thrown is not, which is why that happens in an effect below.
  if (contestValues[0] === null && contestValues[1] === null)
    contestThrown.current = [false, false];
  // …and at every ROUND boundary, so the dice thrown after a tie animate like any other throw.
  // Also idempotent: the second render pass sees the key already stored and does nothing.
  const contestRoundRef = useRef(contestKey);
  if (contestRoundRef.current !== contestKey) {
    contestRoundRef.current = contestKey;
    contestThrown.current = [false, false];
  }
  const throwingNow: readonly [boolean, boolean] = [
    contestValues[0] !== null && !contestThrown.current[0],
    contestValues[1] !== null && !contestThrown.current[1],
  ];
  /*
   * ⚠️ MARKED IN AN EFFECT, NEVER DURING RENDER.
   *
   * This used to flip the ref inline, one line below the read — so the SECOND of StrictMode's two
   * render passes computed `throwingNow = [false, false]` and committed a die with `throwing`
   * already false, i.e. a die that never throws at all. (In production, where StrictMode renders
   * once, the same flip still made `throwing` go true→false on the very next re-render, which is
   * what used to cancel the throw mid-flight — see `useFlyingAtMount` in `backgammon-dice.tsx`.)
   *
   * An effect runs once per COMMIT, after the die has mounted with `throwing` true, so the throw is
   * decided by the frame the value actually appeared in and by nothing else.
   */
  useEffect(() => {
    if (throwingNow[0]) contestThrown.current[0] = true;
    if (throwingNow[1]) contestThrown.current[1] = true;
  });

  /*
   * THE ROLL-OFF VERDICT WAITS FOR THE DIE THAT DECIDES IT.
   *
   * «شروع با تو» used to appear the very frame `opening` landed — and the die that RESOLVES the
   * contest is thrown in exactly that frame, so the board announced the winner while the deciding
   * cube was still tumbling. It reads as a scripted result rather than a throw. The caption now
   * holds until the throw has actually finished (`DIE_THROW_MS`), which still leaves ~2s of the
   * ceremony's own `OPENING_MS` window to read it in.
   *
   * Adopt-at-mount: `contestLanded` starts TRUE, so a resume that arrives mid-ceremony (nothing is
   * being thrown, the dice are already at rest) shows the verdict immediately.
   */
  const [contestLanded, setContestLanded] = useState(true);
  const landingTimer = useRef<number | null>(null);
  const throwKey = `${throwingNow[0] ? 1 : 0}${throwingNow[1] ? 1 : 0}`;
  useEffect(() => {
    if (throwKey === "00") return;
    setContestLanded(false);
    if (landingTimer.current !== null) window.clearTimeout(landingTimer.current);
    landingTimer.current = window.setTimeout(() => setContestLanded(true), DIE_THROW_MS);
  }, [throwKey]);
  useEffect(
    () => () => {
      if (landingTimer.current !== null) window.clearTimeout(landingTimer.current);
    },
    [],
  );

  const bandToast = (): ReactNode => {
    /*
     * §5.1 — the contest outranks everything except a finished دست: until it is decided there is
     * no turn, no dice and nothing on the board to say anything about.
     */
    if (contest && !roundResult) {
      const mineThrown = contestDie(you) !== null;
      return (
        <BandToast tone="info" testId="bg-toast-contest" bandH={fit.bandH}>
          <span className="text-body-md font-bold text-on-surface">
            {contestTied || (contest.ties > 0 && !mineThrown)
              ? fa.backgammon.contestTie
              : mineThrown
                ? fa.backgammon.contestWaiting
                : fa.backgammon.contestThrow}
          </span>
        </BandToast>
      );
    }
    // A finished دست outranks every other message — it is the only thing that matters right now.
    if (roundResult) {
      const iWon = roundResult.winner === you;
      const label =
        roundResult.magnitude === 3
          ? fa.backgammon.roundSagMars
          : roundResult.magnitude === 2
            ? fa.backgammon.roundMars
            : fa.backgammon.roundPlain;
      return (
        <BandToast tone={iWon ? "warn" : "stop"} testId="bg-toast-round" bandH={fit.bandH}>
          <span
            className="block text-body-md font-bold"
            style={{ color: iWon ? "rgb(var(--c-secondary))" : "rgb(var(--c-error))" }}
          >
            {iWon ? fa.backgammon.roundWonYou : fa.backgammon.roundWonOpp}
          </span>
          {/*
           * ⚠️ ONE line, not two. Measured 2026-08-14: three stacked lines make the banner 70px,
           * and the TIGHTEST band this layout produces is 62px (Telegram Desktop 440×640) — so a
           * 3-line banner SPILLS onto the points there. The magnitude and the score share a line.
           */}
          <span className="block text-caption font-bold text-on-surface">
            {label}
            {" · "}
            {/* SAME READING as the header box — «تو ۳ | ۱ حریف», not a bare «۳ — ۱». Two different
                formats for one fact is exactly what «scattered» meant. */}
            <span className="font-numeral tabular-nums text-on-surface-variant">
              {fa.backgammon.you} {faNum(view.score[you] ?? 0)} | {faNum(view.score[oppSeat] ?? 0)}{" "}
              {fa.backgammon.opponent}
            </span>
          </span>
        </BandToast>
      );
    }
    if (replay.draining) return null;
    if (nudge) {
      return (
        <BandToast tone="stop" testId="bg-toast-nudge" bandH={fit.bandH}>
          <span className="text-body-md font-bold text-error">{nudge}</span>
        </BandToast>
      );
    }
    if (!isMyTurn) return null;
    if (view.noLegalMove) {
      return (
        <BandToast tone="stop" testId="bg-toast-blocked" bandH={fit.bandH}>
          {/* card-98 item 6: the notice ALONE — the turn passes on its own, nothing to confirm. */}
          <span className="block text-body-md font-bold text-error">{fa.backgammon.blocked}</span>
        </BandToast>
      );
    }
    if (view.mustConfirm) {
      return (
        <BandToast tone="warn" testId="bg-toast-confirm" bandH={fit.bandH}>
          <span className="block text-body-md font-bold text-secondary">
            {view.diceRemaining.length > 0 ? fa.backgammon.oneDieOnly : fa.backgammon.hintConfirm}
          </span>
        </BandToast>
      );
    }
    if (frozen.length > 0) {
      return (
        <BandToast tone="warn" testId="bg-toast-frozen" bandH={fit.bandH}>
          {/* text-body-md like EVERY other toast. It used to be `text-caption`, so this one message
              rendered a size smaller than the rest and the band's type appeared to change from turn
              to turn. One toast, one size. */}
          <span className="text-body-md font-bold text-secondary">{fa.backgammon.frozenHint}</span>
        </BandToast>
      );
    }
    if (frozenLifted.length > 0) {
      // `info`, not `warn`: nothing is being withheld — the ban lifted, and this says why.
      return (
        <BandToast tone="info" testId="bg-toast-frozen-lifted" bandH={fit.bandH}>
          <span className="text-body-md font-bold text-secondary">
            {fa.backgammon.frozenLiftedHint}
          </span>
        </BandToast>
      );
    }
    if (view.mustEnterFromBar) {
      return (
        <BandToast tone="info" testId="bg-toast-bar" bandH={fit.bandH}>
          <span className="text-body-md font-bold text-on-surface">{fa.backgammon.hintBar}</span>
        </BandToast>
      );
    }
    if (canRoll) {
      return (
        <BandToast tone="info" testId="bg-toast-roll" bandH={fit.bandH} fit>
          <span className="text-body-md font-bold text-on-surface">{fa.backgammon.hintRoll}</span>
        </BandToast>
      );
    }
    /*
     * THE MOVE HINT — restored 2026-08-09.
     *
     * The old board carried a persistent line under the felt («حرکت با ۵ و ۶ — یک مهرهٔ روشن رو
     * بزن»), described in its own comment as "the core cure for 'nothing to do / frozen board'".
     * Moving the messages into the band dropped it, which left an ordinary turn with dice on the
     * felt, glowing checkers and NOT ONE WORD of instruction. It belongs in the band like the rest.
     */
    /* ⚠️ AND NOT WHILE THE DICE ARE STILL IN THE AIR (`diceRead`). This line names the roll in
       WORDS — «حرکت با ۴ (۴ بار)» — so on a double it gives the throw away even more plainly than
       the ×N badge does. The band simply stays empty for the ~1s of the throw, which is what a
       physical table shows too: nothing is decided until the cubes stop. */
    if (diceRead && view.diceRemaining.length > 0 && view.legalFrom.length > 0) {
      /* A DOUBLE is one number played several times, and joining the remaining dice read
         «حرکت با ۶ و ۶ و ۶ و ۶» — which a player reads as «شش، چهار بار». So say that. */
      const first = view.diceRemaining[0];
      const dice =
        view.isDoubles && first !== undefined
          ? fa.backgammon.diceTimes(faNum(first), faNum(view.diceRemaining.length))
          : view.diceRemaining.map(faNum).join(" و ");
      return (
        <BandToast tone="info" testId="bg-toast-move" bandH={fit.bandH}>
          <span className="text-body-md font-bold text-on-surface">
            {fa.backgammon.hintMove.replace("{dice}", dice)}
          </span>
        </BandToast>
      );
    }
    return null;
  };

  /*
   * ⚠️ A SHARED WINDOW (seat < 0) BELONGS TO BOTH PANELS.
   *
   * §5.1's opening contest is a SIMULTANEOUS phase — either player may throw, in either order — so
   * the server broadcasts ONE window for the whole phase under `SHARED_TURN_SEAT` (-1), exactly as
   * بی‌دل's 3-card pass does. Matching it against a real seat number (`timer.seat === seat`) never
   * matches, so NEITHER panel drew a ring and the contest ran its whole budget with nothing on
   * screen saying a deadline existed. That silence is most of why an unthrown die read as «the
   * table is stuck forever» on a device.
   */
  // …read off the window the ring is ALLOWED to draw (see the reveal gate), never the live prop:
  // the roll's own window is withheld until the dice it was opened by have finished landing.
  const adopted = shownTimer.current;
  /*
   * …and EASED onto it when a double has just stretched the turn. Interpolating the two window
   * FIELDS rather than the two readings keeps `TurnRing` a pure function of (endsAt, totalMs): at
   * p=0 it draws exactly the old window, at p=1 exactly the new one, and every frame between is a
   * real window, so the digits tick up and the arc sweeps instead of snapping. Skipped entirely
   * under reduced motion — the grant is still NAMED there, it just arrives at once.
   */
  const shown = ((): BgTurnTimer | null => {
    if (adopted === null || granted === null || reduced) return adopted;
    const p = Math.min(1, Math.max(0, (nowMs - granted.at) / RING_GROW_MS));
    if (p >= 1) return adopted;
    const e = 1 - Math.pow(1 - p, 3); // ease-out cubic
    const from = granted.from;
    const mix = (a: number, b: number): number => a + (b - a) * e;
    return {
      ...adopted,
      endsAt: mix(from.endsAt, adopted.endsAt),
      totalMs: mix(
        from.totalMs ?? from.totalSec * 1000,
        adopted.totalMs ?? adopted.totalSec * 1000,
      ),
    };
  })();
  const sharedWindow = shown !== null && shown.seat < 0;
  const timedSeat = (seat: 0 | 1): boolean => !!shown && (sharedWindow || shown.seat === seat);

  const ringFor = (seat: 0 | 1): ReactNode =>
    shown ? (
      <TurnRing
        endsAt={timedSeat(seat) ? shown.endsAt : null}
        totalSec={shown.totalSec}
        totalMs={shown.totalMs}
        active={timedSeat(seat) && view.status === "playing"}
        grantedSec={granted !== null && timedSeat(seat) ? granted.sec : null}
      />
    ) : null;

  const strikesFor = (seat: 0 | 1): ReactNode =>
    strikes ? <StrikeDots used={strikes[seat] ?? 0} of={STRIKE_LIMIT} /> : null;

  return (
    <div
      className="mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{ height: "var(--game-h)", background: "var(--room-bg)" }}
    >
      {/* HUD — exit (right in RTL) · centred «تخته نرد» · the دست counter.
          A 3-COLUMN GRID with equal 1fr flanks, so the title stays exactly centred whatever the
          round chip's width is; `justify-between` would have shifted it as «دست ۱۰» grew.
          `data-table-hud` marks it as the table's header band (it is not a `<header>`): the end
          card's shell starts BELOW it, so the score box is never covered or dimmed. */}
      <div
        data-table-hud=""
        className="grid flex-none items-center px-4 pb-1"
        style={{
          gridTemplateColumns: "1fr auto 1fr",
          // 10px, matching منچ and — crucially — the shell's fixed chat toggle, which pins
          // itself at `10px + safe` against a 440px-wide centred column. At 8px in a 460px
          // column the two header buttons sat 2px apart vertically on every viewport, and the
          // gap between them drifted from 8px to 18px on a 480px-wide window (measured).
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <span className="flex justify-start">
          <button
            type="button"
            onClick={onLeave}
            aria-label={fa.backgammon.leave}
            className={`${BTN} flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>
        </span>
        <span className="font-headline-md text-headline-sm font-bold text-secondary">
          {fa.backgammon.title}
        </span>
        {/* THE MATCH, IN ONE PLACE — both totals and which دست we are on. A match runs to a POINT
            total (مارس pays 2, سگ‌مارس 3), so the round number alone says little; the two live
            together or neither is legible. Meaningless in a single دست, so nothing is drawn there. */}
        <span className="flex justify-end">
          {view.matchTo > 1 ? (
            <BgScoreBox
              mine={view.score[you] ?? 0}
              theirs={view.score[oppSeat] ?? 0}
              round={view.round}
              target={view.matchTo}
            />
          ) : null}
        </span>
      </div>

      {/* TABLE GROUP — opponent panel · board · (my panel + action). The board takes everything
          left over, and `boardGeometry` spends it on the MIDFIELD BAND rather than on longer
          points, which is what lets the board fill the screen without becoming needles. */}
      <div
        ref={box.ref}
        className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-1"
        /* ⚠️ THE BOTTOM INSET. Every other table pads by the home-indicator inset; this one did
           not, so the player's own panel sat against the very edge of the screen — measured 4px of
           gap at 360×640 in a harness that reports a ZERO inset, i.e. UNDER the indicator on a real
           phone. `boardGeometry` is given the same number so the board shrinks to make room rather
           than the panel being pushed off. */
        style={{ paddingBottom: `calc(${BOARD_PAD_BOTTOM}px + var(--game-safe-bottom))` }}
      >
        <div className="w-full">
          <BgPanel
            mine={false}
            parts={opponent}
            active={view.status === "playing" && view.turn === oppSeat}
            ring={ringFor(oppSeat)}
            strikes={strikesFor(oppSeat)}
            handover={replay.event?.kind === "handover" && replay.event.to === oppSeat}
          />
        </div>

        {/* THE BOARD — a wooden rim with a rail top and bottom (each holding a bear-off pocket) and
            the felt between them. */}
        <div
          ref={boardRef}
          className="bg-board"
          style={
            {
              "--pt-w": "22px",
              width: fit.w,
              height: fit.h,
              maxWidth: "100%",
              flex: "none",
              position: "relative",
              overflow: "hidden",
              borderRadius: 10,
              display: "flex",
              flexDirection: "column",
              padding: `0 ${RIM_X}px`,
            } as CSSProperties
          }
        >
          {/* Opponent's rail + pocket. */}
          {rail(oppSeat)}

          <div
            className="bg-felt"
            style={{
              flex: "none",
              height: fit.feltH,
              position: "relative",
              display: "flex",
              overflow: "hidden",
              borderRadius: 3,
            }}
          >
            {/* DOM ORDER IS LOAD-BEARING: the app is RTL, so the FIRST half renders on the visual
                RIGHT. That is what puts each viewer's home board (points 0..5, last in the `bottom`
                array) on the visual LEFT — the same side as their pocket. Do not reorder. */}
            {half(0)}

            {/* THE CENTRAL BAR — a plain recessed channel. The two bar SOURCES (opponent above,
                yours below) overlay it so a barred checker stays tappable.
                ⚠️ NOTHING DECORATIVE GOES IN HERE. It used to carry two brass «hinges» of a folding
                تخته: small gold rounded pieces, on the one surface of the board where a gold object
                means «this checker must be entered before you may do anything else». Reported as
                «two yellow checker-like ornaments» — a decoration that can be mistaken for a rule
                is worse than no decoration. The channel's own recess is what says «bar». */}
            <div
              className="bg-spine"
              style={{
                width: fit.spineW,
                flexShrink: 0,
                height: "100%",
                position: "relative",
                zIndex: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "3px 0",
                }}
              >
                {barBtn(oppSeat)}
                {barBtn(you)}
              </div>
            </div>

            {half(6)}

            {/* ── THE MIDFIELD BAND ──────────────────────────────────────────────────
                An overlay pinned to the empty strip between the two rows of points. The dice rest
                on the physical RIGHT, messages appear on the physical LEFT, and NOTHING else is
                ever drawn here — which is why a die can no longer sit on a checker, on a point or
                on the spine. `pointer-events: none` so the felt underneath stays tappable. */}
            <div
              data-testid="bg-band"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: fit.pointH,
                height: fit.bandH,
                zIndex: 20,
                display: "flex",
                alignItems: "center",
                pointerEvents: "none",
              }}
            >
              {/*
                §5.1 — THE CONTEST AND THE OPENING TURN SHARE ONE SLOT PER SEAT.

                Each seat's die sits in its OWNER's own half, and it stays there: empty while that
                player still owes a throw, thrown once when it lands, then held exactly where it
                landed through the roll-off announcement AND the whole opening turn (the Iranian
                rule plays the contest dice, so they never needed to move). The React key is per
                SEAT, so nothing re-mounts across those transitions — which is what makes the die
                animate exactly once instead of three times.
              */}
              {contest || opening
                ? ([you, oppSeat] as const).map((seat) => {
                    const isMine = seat === you;
                    // Live contest → that seat's single die (null until it throws). Opening turn →
                    // the same value, now one of the two playing dice. The deciding pair can never
                    // be equal (a tie re-throws), so matching by VALUE names the owner exactly.
                    const value = contest
                      ? (contest.dice[seat] ?? contest.tied?.[seat] ?? null)
                      : (view.opening?.[seat] ?? null);
                    const k = contest ? -1 : feltDice.findIndex((d) => d === value);
                    const spent = !contest && k >= 0 && !diceAreStale && dieSpent(k);
                    const consuming = !contest && !spent && activeDieValue === value;
                    const wonContest = opening !== null && opening.winner === seat && contestLanded;
                    return (
                      <div
                        key={`contest-${seat}-${contestKey}`}
                        data-testid={isMine ? "bg-contest-you" : "bg-contest-opp"}
                        data-thrown={value === null ? "0" : "1"}
                        style={{
                          position: "absolute",
                          ...(isMine ? { right: contestInset } : { left: contestInset }),
                          width: contestSlotW,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          // Only the ceremony compares the two dice, so only it dims the LOSER.
                          // Otherwise the pair dims together once it is STALE — the turn is over,
                          // the engine has cleared `dice` and we are holding the last roll for
                          // display, exactly as the tray does. Without it a spent opening pair
                          // reads as a live one right up until the next player throws.
                          opacity:
                            opening !== null && contestLanded && !wonContest
                              ? 0.45
                              : diceAreStale && !contest
                                ? 0.5
                                : 1,
                          transition: "opacity 400ms linear",
                        }}
                      >
                        {value === null ? (
                          /* An EMPTY slot, so the board says «one die each» before either lands. */
                          <span
                            aria-label={
                              isMine ? fa.backgammon.contestThrow : fa.backgammon.contestWaiting
                            }
                            style={{
                              width: dieSize,
                              height: dieSize,
                              borderRadius: Math.max(4, Math.round(dieSize * 0.18)),
                              border: "2px dashed rgb(var(--c-outline-variant))",
                              opacity: 0.55,
                            }}
                          />
                        ) : (
                          <span className={consuming ? "bg-die-active" : ""}>
                            <Die3D
                              value={value}
                              size={dieSize}
                              seed={`contest:${seat}:${value}`}
                              /* Thrown ONCE, when THIS die lands — including the one that
                                 resolves the contest. Never again: not for the announcement, not
                                 when the pair becomes the playing roll. */
                              throwing={throwingNow[seat]}
                              fromBottom={isMine}
                              launch={Math.max(48, Math.round(fit.feltH * 0.22))}
                              spent={spent}
                              alwaysDrawn
                              testId={isMine ? "contest-die-you" : "contest-die-opp"}
                            />
                          </span>
                        )}
                        {/* The roll-off's verdict, under the winning die — the ceremony is now a
                            CAPTION over dice already at rest, not a second pair of throws. */}
                        <span
                          className="font-bold text-secondary"
                          style={{ fontSize: 11, lineHeight: "13px", whiteSpace: "nowrap" }}
                        >
                          {wonContest
                            ? opening!.winner === you
                              ? fa.backgammon.openingYou
                              : fa.backgammon.openingOpp
                            : "\u200b"}
                        </span>
                      </div>
                    );
                  })
                : null}

              {/*
                MESSAGES.

                While the two dice sit in OPPOSITE halves (the contest and the opening turn) the
                toast is CENTRED BETWEEN THEM, over the bar — there is no free half to put it in,
                and the old rule («the half the dice are not in») dropped it straight on top of the
                opponent's own die slot. It may cross the bar; it may not cover a die, so it is
                narrower here and the dice halves were tightened to 40% to leave it room.

                Once the pair gathers into one half (turn two onward) the original rule returns:
                the toast takes the OTHER half, so the two can never collide.
              */}
              <div
                data-testid="bg-band-message"
                data-place={contest || opening ? "center" : rollerIsMe ? "left" : "right"}
                style={
                  contest || opening
                    ? {
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        maxWidth: CONTEST_TOAST_MAX,
                        display: "flex",
                        justifyContent: "center",
                      }
                    : {
                        position: "absolute",
                        ...(rollerIsMe ? { left: 4 } : { right: 4 }),
                        // 44%, not 48%: each half must clear the SPINE, or a wide pair of dice can
                        // reach the bar again — the exact overlap this band exists to remove.
                        width: "44%",
                        display: "flex",
                        justifyContent: "center",
                      }
                }
              >
                {bandToast()}
              </div>

              {/*
                DICE — in the ROLLER'S OWN RIGHT-HAND HALF (the traditional rule: your dice land on
                your right). The board is drawn from the viewer's seat, so the viewer's right IS the
                physical right — and the opponent, sitting opposite, has their right on the physical
                LEFT. Physical `left`/`right`, never `inset-inline-*`: the documented RTL trap.
                Either way they stay inside the band, so they can never touch a checker.
              */}
              <div
                data-testid="dice-tray"
                data-side={rollerIsMe ? "right" : "left"}
                style={{
                  position: "absolute",
                  ...(rollerIsMe ? { right: 4 } : { left: 4 }),
                  // 44%, not 48%: each half must clear the SPINE, or a wide pair of dice can
                  // reach the bar again — the exact overlap this band exists to remove.
                  width: "44%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  opacity: diceAreStale ? 0.5 : 1,
                  transition: "opacity 300ms linear",
                }}
              >
                {/* Withheld during the opening ceremony — the band holds one pair of dice at a
                    time, never two (the "why are there four dice?" defect). */}
                {(opening || contest ? [] : feltDice.slice(0, 2)).map((d, k) => {
                  const spent = !diceAreStale && dieSpent(k);
                  const consuming = !spent && activeDieValue === d;
                  return (
                    <span
                      key={`${rollNo.current}-${k}`}
                      className={consuming ? "bg-die-active" : ""}
                    >
                      <Die3D
                        value={d}
                        size={dieSize}
                        seed={`${rollNo.current}:${k}:${d}`}
                        throwing={thrown.current}
                        fromBottom={rollerIsMe}
                        launch={Math.max(48, Math.round(fit.feltH * 0.22))}
                        spent={spent}
                        alwaysDrawn
                        testId={`die-${k}`}
                      />
                    </span>
                  );
                })}
                {/* ⚠️ GATED ON THE SAME CONDITION AS THE DICE. It used to be gated only on
                    `view.dice.length === 4`, so while the halves held the contest pair the tray
                    rendered a bare «×۴» beside two numbers from a different roll — the reported
                    «next to the 3 the number 4 was displayed». A count with nothing to count is
                    never drawn. */}
                {/* ⚠️ AND WITHHELD UNTIL THE THROW ENDS (`diceRead`). This count is the loudest
                    thing on the board: it announces «this is a double» a full second before the
                    cubes it is counting have stopped moving. See the reveal gate above. */}
                {diceRead && !opening && !contest && view.dice.length === 4 ? (
                  <span
                    data-testid="doubles-badge"
                    aria-label={`دوبل، ${faNum(view.diceRemaining.length)} حرکت مانده`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      height: 24,
                      borderRadius: 999,
                      background: "rgb(var(--c-secondary))",
                      color: "rgb(var(--c-ink-on-gold))",
                      fontWeight: 800,
                      fontSize: 12,
                      boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
                    }}
                  >
                    {`×${faNum(view.diceRemaining.length)}`}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* My rail + pocket. */}
          {rail(you)}
        </div>

        {/* The travelling checker — a fixed-position overlay above the whole board, so a bar entry
            and a bear-off animate exactly like a point-to-point move. */}
        {flights.map((f) => (
          <FlyingChecker
            key={f.key}
            from={f.from}
            to={f.to}
            mine={f.mine}
            size={checkerPx}
            knock={f.knock}
          />
        ))}

        {/* MY PANEL + THE ACTION SLOT. Both are FIXED height and the slot is ALWAYS reserved, so
            nothing here can change the board's box between turns (the measured 49px of swing that
            used to resize the board every turn). */}
        <div className="flex w-full items-stretch gap-2">
          <div className="min-w-0 flex-1">
            <BgPanel
              mine
              parts={self}
              active={view.status === "playing" && view.turn === you}
              ring={ringFor(you)}
              strikes={strikesFor(you)}
              handover={replay.event?.kind === "handover" && replay.event.to === you}
            />
          </div>
          {/* UNDO gets its OWN always-reserved cell, so «تأیید» never has to share the primary slot
              with it and the primary therefore never moves between phases. */}
          <div
            data-testid="bg-undo-slot"
            style={{ width: UNDO_W, height: PANEL_H, flex: "none" }}
            className="flex items-center justify-center"
          >
            {/* ⚠️ THE TURN CLAUSE IS LOAD-BEARING. `staged` is NOT gated on the mover in the
                engine's `viewFor` — the board needs the OPPONENT's pending sub-moves to walk
                their turn — so «something is staged» is true on their turn too, and this cell
                used to offer an undo beside my own profile while they were mid-move (measured on
                the deployed server: 30 of 101 frames of one solo game).

                It is NOT `legal.some(m => m.kind === "undo")` like every other control here:
                the engine's `legalMoves` never enumerates `undo` (only `isLegal` accepts it), so
                that test is always false and would remove the button entirely. */}
            {view.turn === you && view.staged.length > 0 && !replay.draining ? (
              <button
                type="button"
                onClick={() => onMove({ kind: "undo" })}
                aria-label={fa.backgammon.undo}
                title={fa.backgammon.undo}
                data-testid="bg-undo"
                className={`${BTN} flex h-11 w-11 items-center justify-center rounded-full border border-outline-variant bg-surface-container-high text-on-surface-variant shadow-sm active:scale-95`}
              >
                {/* A U-turn arrow, NOT `GameIcon type="back"` — that is a chevron, and in RTL a
                    chevron reads as "forward". Undo has to look like undo. */}
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="none">
                  <path
                    d="M9 14L4 9l5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 9h10a6 6 0 0 1 0 12h-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
          <div
            data-testid="bg-action-slot"
            style={{ width: ACTION_W, height: PANEL_H, flex: "none" }}
          >
            {replay.draining ? null : canOpeningRoll ? (
              /* §5.1 — your own single die. The same control as a roll, because that is what it is. */
              <Button
                variant="primary"
                silent
                onClick={() => onMove({ kind: "openingRoll" })}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  padding: 0,
                }}
              >
                <span style={{ fontSize: 19, lineHeight: 1 }}>🎲</span>
                <span style={{ fontSize: 12 }}>{fa.backgammon.roll}</span>
              </Button>
            ) : canRoll ? (
              <Button
                variant="primary"
                /* The throw has its own voice (dice on the felt), so the generic UI tap would be a
                   second sound for one action. */
                silent
                onClick={() => onMove({ kind: "roll" })}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  padding: 0,
                }}
              >
                <span style={{ fontSize: 19, lineHeight: 1 }}>🎲</span>
                <span style={{ fontSize: 12 }}>{fa.backgammon.roll}</span>
              </Button>
            ) : view.mustConfirm && canConfirm && !view.noLegalMove ? (
              /* INSTANT confirm — the turn commits the moment it is pressed, and only a turn the
                 player has STAGED offers it: waiting to be allowed to finish a turn you have already
                 decided is dead time. A BLOCKED turn shows none (card-98 item 6): the server passes
                 a no-roll turn, and the board's own silent confirm above ends a dance. */
              <Button
                variant="primary"
                onClick={() => onMove({ kind: "confirm" })}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  padding: 0,
                }}
              >
                <GameIcon type="check" size={19} />
                <span style={{ fontSize: 12 }}>{fa.backgammon.confirmShort}</span>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ITEM 12 — the تخته نرد checker-colour picker.
 *
 * Offered on the offline start screen and in the online table settings, mirroring منچ's colour
 * choice. Each swatch is the REAL chip gradient rather than a flat approximation, so what you
 * pick is exactly what lands on the board.
 */
export function BackgammonColorSelect({
  value,
  onChange,
}: {
  value: BackgammonCheckerColor;
  onChange: (value: BackgammonCheckerColor) => void;
}): ReactNode {
  const swatch = (color: BackgammonCheckerColor): ReactNode => {
    const on = value === color;
    return (
      <button
        key={color}
        type="button"
        role="radio"
        aria-checked={on}
        aria-label={color === "gold" ? fa.backgammon.colorGold : fa.backgammon.colorObsidian}
        data-testid={`bg-color-${color}`}
        onClick={() => onChange(color)}
        className={`${BTN} flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2 transition-all ${
          on
            ? "border-secondary bg-secondary-container/40 shadow-[0_0_10px_rgb(var(--c-secondary)/0.35)]"
            : "border-outline-variant bg-surface-container-low"
        }`}
      >
        <span
          className="h-6 w-6 flex-none rounded-full border"
          style={{
            background:
              color === "gold"
                ? "radial-gradient(circle at 30% 30%, #ffe088 0%, #af8d11 70%, #574500 100%)"
                : "radial-gradient(circle at 30% 30%, #31353a 0%, #0b0f12 70%, #000000 100%)",
            borderColor: color === "gold" ? "#7a5e0b" : "#404943",
          }}
        />
        <span
          className={`text-body-md font-bold ${on ? "text-secondary" : "text-on-surface-variant"}`}
        >
          {color === "gold" ? fa.backgammon.colorGold : fa.backgammon.colorObsidian}
        </span>
      </button>
    );
  };
  return (
    <div role="radiogroup" aria-label={fa.backgammon.color} className="flex w-full gap-2">
      {swatch("gold")}
      {swatch("obsidian")}
    </div>
  );
}
